import AlertIcon from '@assets/icons/alert.svg';
import ChevronDown from '@assets/icons/chevron-down.svg';
import DeleteIcon from '@assets/icons/delete.svg';
import i18n from '@/utils/i18n';
import MenuHorizontalIcon from '@assets/icons/menu-horizontal.svg';
import moment from 'moment';
import OfflineModeIcon from '@assets/icons/offline-mode.svg';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { AllCartsSheet } from './multi-cart/all-carts-sheet';
import {
  AllowedShortcutNumbers,
  AllowedShortcuts,
  KeyboardScope,
  numberKeyMap,
} from '@/platform/shortcuts/operator-shortcuts';
import { Analytics } from '@/utils/analytics';
import { ApolloError, gql, useMutation } from '@apollo/client';
import {
  APP_CONTAINER_ROUTES,
  CONTAINER_MODAL_ROUTES,
  NEW_ORDER_ROUTES,
  ORDER_INBOUNDS_ROUTES,
  SMART_ORDER_CONTAINER_ROUTES,
  SUBSTITUTION_ROUTES,
} from '@/navigators/routes';
import {
  AppliedVolumePromotion,
  OrderSummaryPromotions,
} from '@/components/orders/order-summary-promotions';
import {
  AppModalStackParams,
  EditOrderItemNoteParams,
  NewOrderStackParams,
} from '@/navigators/types';
import {
  BackHandler,
  InteractionManager,
  Platform,
  StatusBar,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import { Button } from '@/components/button';
import { captureException } from '@/utils/sentry';
import { CartFormActionSheet } from './multi-cart/cart-form-action-sheet';
import {
  cartHasLeadTimeItem,
  findAvailabilityDay,
  FULFILLMENT_SHIPPING_TYPES,
  getOrderFulfillmentDates,
  getValidFulfillment,
  isFulfillmentStale,
  UNPLACED_ORDER_STATUSES,
} from '@domain/fulfillment-settings/blocs/order-availability';
import { createStyles } from '@/contexts/theme-context';
import {
  displayCurrencyPlaceholder,
  displayTotalPrice,
  formatCurrency,
  getTotalLineItemPrice,
  MICRO_CONVERSION,
  OrderFee,
  roundMicrosToNearestWholeCent,
} from '@/utils/money';
import { EditFulfillmentDateButton } from '@/components/orders/edit-fulfillment-date-button';
import {
  EmployeeRole,
  FulfillmentName,
  NewOrder_OrderFieldsFragment,
  Order_By,
  OrderConstraintUnit,
  OrderFeeDisplayType,
  OrderStatus,
  OrderSummary_ItemListFieldsFragment,
  OrderSummary_ValidateOrderMutation,
  Substitutions_ClearItemSubstitutionMutation,
  Substitutions_ClearItemSubstitutionMutationVariables,
  ValidateOrderAlert,
} from '@/types';
import { EssentialItemReminderSheet } from '@/components/orders/essential-item-reminder-sheet';
import { FallbackConfirmationActionSheet } from '@/components/fallback-confirmation-action-sheet';
import {
  Feature,
  setFeatureSpanAttributes,
  SpanAttribute,
} from '@/utils/sentry/attributes';
import {
  FONTS,
  LIST_MARGIN_HORIZONTAL,
  SCREEN_PADDING,
  SCREEN_PADDING_LARGE,
  shadow,
  TYPOGRAPHY,
} from '@/styles';
import { formatSummaryDate } from '@/utils/date';
import {
  FulfillmentActionSheet,
  FulfillmentActionSheetRoute,
} from '@/components/partners/fulfillment-action-sheet';
import { FulfillmentButton } from '@/components/partners/fulfillment-button';
import { FulfillmentPickerDropdown } from '@/components/partners/fulfillment-picker-dropdown';
import {
  getCodeFromGQLError,
  getMessageFromGQLError,
} from '@/utils/apollo-client';
import { getOrderSummaryInputError } from '@/utils/new-order';
import { getSearchFieldsFromMetadata } from '@/contexts/item-context-utils';
import { getTrueTime } from '@/utils/true-time';
import {
  groupBySubstitution,
  ItemWithSubstitute,
  OriginalVariantPack,
  SubstitutionType,
} from '@/components/orders/substitution-item-wrapper';
import {
  InputErrorMap,
  OrderSummaryInputs,
} from '@/components/orders/order-summary-inputs';
import {
  InvoiceUnpaidItemType,
  PaymentMethodOption,
  usePaymentsContext,
} from '@/contexts/payments-context';
import { ItemListOption } from '@/contexts/item-context-types';
import { Key } from '@platform/shortcuts/utils/keyboard-platform';
import {
  LeadTimeSplitBehavior,
  OrderSummaryInputID,
  OrderValidationErrorCode,
  PaymentItemType,
  PaymentMethodType,
  PoNumberDisplay,
  SubstitutionAction,
  ViewableBy,
} from '@/enums';
import { ListItemSeparator } from '@/components/list-item';
import { ListSection, RecyclerListRef } from '@/components/recycler-list';
import {
  mergeOrderNotesIntoValidateOrderData,
  useAdaptOrderItemToItemListOption,
} from '@/utils/order-summary';
import { MultiStyleText } from '@/components/multi-style-text';
import { Props as NavigationHeaderProps } from '@/components/navigation-header';
import { NavigationProp, RouteProp } from '@react-navigation/native';
import {
  OFFLINE_UPDATE_QUANTITY_DEBOUNCE_DELAY_MS,
  useNewOrderActionsContext,
} from '@/contexts/new-order-actions-context';
import { ORDER_FRAGMENT } from '@/contexts/new-order-context-queries';
import { OrderPaymentMethodSelector } from '@/components/orders/order-payment-method-selector';
import { OrderQuantityActions } from './hooks/use-quantity-reducer';
import { OrderQueuedActionSheet } from '@/components/order-queued-action-sheet';
import { OrderSubmittedActionSheet } from '@/components/order-submitted-action-sheet';
import { OrderSubmittedComponentProps } from '@/utils/order-submitted';
import { OrderSummaryFooter } from '@/components/orders/order-summary-footer';
import { OrderSummaryInput } from '@domain/configuration/types';
import { OrderSummaryItemPromotions } from '@/components/orders/order-summary-list-item';
import {
  OrderSummaryList,
  SECTION_BORDER_RADIUS,
  SectionMetadataMap,
} from '@/components/orders/order-summary-list';
import { OrderSummaryListHeader } from './order-summary-list-header';
import { OrderSummaryListItemType } from '@/components/orders/order-summary-main-content';
import { OrderSummaryRecommendations } from '@/components/orders/order-summary-recommendations';
import { OrderSummarySettingsActionSheet } from './order-summary-settings-action-sheet';
import { PageContainer } from '@/components/page-container';
import { PerfTiming, PerfTimingEvent } from '@/utils/perf-timing';
import { postPunchoutOrderMessage } from '@/utils/punchout';
import { PromotionDiscountType } from '@/utils/volume-promotions-types';
import {
  QuantityOverrides,
  SelectedFulfillmentType,
} from '@/contexts/new-order-actions-context-types';
import { SameDayConfirmationActionSheet } from '@/components/same-day-confirmation-action-sheet';
import { SubstitutionChoiceParams } from '@/components/substitutions/types';
import { SubstitutionConfirmationActionSheet } from '@/components/substitutions/substitution-confirmation-action-sheet';
import {
  Timeslot,
  TimeslotOption,
} from '@domain/fulfillment-settings/types/types';
import { TimeToFullDisplay } from '@/utils/sentry/platform';
import { translateFulfillmentType } from '@domain/fulfillment-settings/utils/translate-fulfillment-type';
import { Type } from '@/components/toast';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useActionSheetManagerContext } from '@/contexts/action-sheet-manager-context';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useBaseItemQueryVariables } from '@/hooks/use-item-query-variables';
import { useCartFlow } from './multi-cart/hooks/use-cart-flow';
import { useCompleteInboundReview } from '@/hooks/use-complete-inbound-review';
import { useComponentFocus } from '@/hooks/use-component-focus';
import { useDebouncedCallback } from 'use-debounce';
import {
  useDynamicConfigValue,
  useSubstitutionCatalogSearchEnabled,
  useSupplierDynamicConfigValue,
} from '@/hooks/use-dynamic-config-value';
import { useGetOfflineTotalPrice } from '@/hooks/use-get-offline-total-price';
import { useGetSubstitutionOptions } from '@/components/substitutions/hooks/use-get-substitution-options';
import { useItemLeadTimeContext } from '@/contexts/item-lead-time-context';
import { useItemRecommendationsContext } from '@/contexts/item-recommendations-context';
import { useKeyboardHotkeysContext } from '@/contexts/keyboard-shortcuts/keyboard-shortcuts-context';
import { useKeyboardShortcuts } from '@platform/shortcuts/hooks/use-keyboard-shortcuts';
import { useLayoutContext } from '@/contexts/layout-context';
import { useMultiSearchFilters } from '@/hooks/use-multi-search-filters';
import { useNavigateToEditOrderItemNote } from '@/hooks/use-edit-order-item-note-navigation';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useNewOrderLoadingContext } from '@/contexts/new-order-loading-context';
import { useNewOrderQuantityStore } from '@/stores/new-order-quantity-store';
import { useOfflineDraftOrderToOrderData } from '@/hooks/use-offline-draft-order-to-order-data';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import {
  useOfflineOrderContext,
  useOfflineQueuedOrders,
} from '@/hooks/use-offline-data';
import { useOfflinePriceEdits } from '@/hooks/use-offline-price-edits';
import { useOrderAlreadyPlacedToast } from '@/hooks/use-order-already-placed-toast';
import { useOrderInboundsStore } from '@/stores/order-inbounds-store';
import { useOrderSummaryListLayout } from '@/hooks/use-order-summary-list-layout';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSetItemSubstitution } from '@/components/substitutions/hooks/use-set-substitution';
import { useSmartOrderParams } from '@/hooks/use-smart-order-params';
import { useSupplierOrderAvailability } from '@/hooks/use-supplier-order-availability';
import { useToasts } from '@/contexts/toast-context';
import { useTransactionFees } from '@/hooks/use-transaction-fees';
import { useValidateOrder } from '@/hooks/use-validate-order';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';

const getSearchableFields = (item: OrderSummaryListItemType) => {
  return (
    item.metadata?.orderItems.flatMap((oi) =>
      getSearchFieldsFromMetadata(oi.item),
    ) ?? []
  );
};

const UPDATE_CART_DEBOUNCE_DELAY_MS = 2000;
const AUSTRALIA_EFTPOS_SUBTYPE = 'EFTPOS_AUSTRALIA';
const VALIDATION_INTERACTION_TIMEOUT_MS = 5000;

export const CLEAR_ITEM_SUBSTITUTION_MUTATION = gql`
  mutation Substitutions_ClearItemSubstitution(
    $orderUUID: uuid!
    $sourceVariantPackUUID: uuid!
  ) {
    clearItemSubstitution(
      order_id: $orderUUID
      source_variant_pack_id: $sourceVariantPackUUID
    ) {
      order {
        ...NewOrder_OrderFields
      }
    }
  }
  ${ORDER_FRAGMENT}
`;

interface TotalPricesMap {
  baseAmountInMicros: number;
  discountedAmountInMicros: number;
}

export interface ChooseSubstitutionParams {
  fulfillmentDate: string;
  itemWithSubstitute: ItemWithSubstitute<ItemListOption>;
  orderUUID: string;
  variantPacksInOrder: Set<string>;
}

interface ComponentProps {
  chooseSubstitution: (params: ChooseSubstitutionParams) => Promise<void>;
  clearInitialSubstitutionChoice: () => void;
  editOrderItemNote: (params: EditOrderItemNoteParams) => void;
  hasFocus: boolean;
  initialSubstitutionChoice?: SubstitutionChoiceParams;
  loadingPaymentMethod?: boolean | null;
  navigateBack: () => void;
  navigateToPaymentMethod: () => void;
  navigationHeaderProps: NavigationHeaderProps;
  onOrderPlaced: (props: OrderSubmittedComponentProps | null) => void;
  selectedPaymentMethod?: PaymentMethodOption | null;
  setCloseable: (closable: boolean) => void;
  skipListInteractionCheck?: boolean;
  smartOrderUUID: string | null;
}

export const OrderSummaryComponent: React.FC<ComponentProps> = ({
  chooseSubstitution,
  clearInitialSubstitutionChoice,
  editOrderItemNote,
  hasFocus,
  initialSubstitutionChoice,
  loadingPaymentMethod,
  navigateBack,
  navigateToPaymentMethod,
  navigationHeaderProps,
  onOrderPlaced,
  selectedPaymentMethod,
  setCloseable,
  skipListInteractionCheck,
  smartOrderUUID,
}) => {
  const [isOrderSummaryConfigOpen, setIsOrderSummaryConfigOpen] =
    useState(false);
  const reopenSettings = useCallback(
    () => setIsOrderSummaryConfigOpen(true),
    [],
  );
  const {
    cancelCartFlow,
    closeAllCarts,
    completeCartFlow,
    editingCart,
    isAllCartsOpen,
    isCartFormOpen,
    openAllCarts,
    openEditCart,
    openNewCart,
  } = useCartFlow({ reopenSettings });
  const [orderSummaryVisible, setOrderSummaryVisible] = useState(false);
  const { bottom } = useSafeAreaInsets();
  const [styles, theme] = useStyles();
  const windowDimensions = useWindowDimensions();
  const { isSmallLayout } = useLayoutContext();

  const { enableScope, disableScope } = useKeyboardHotkeysContext();
  const itemQueryVariables = useBaseItemQueryVariables();
  // Only saved quantities are read at the screen level (for section totals).
  // savedQuantityMap keeps a stable identity across local quantity edits — it
  // only changes on hydrate — so typing in a quantity doesn't re-render the
  // screen or recompute sectionMetadataMap. Rows subscribe to their own
  // quantities through the store.
  const savedQuantityMap = useNewOrderQuantityStore(
    (s) => s.quantityState.savedQuantityMap,
  );
  const {
    activeOrderUUID,
    availableOrderFulfillments,
    dispatchQuantityReducerAction,
    dispatchQuantityUpdateAction,
    draftOrder,
    multiCartEnabled,
    poNumberDisplay,
    refetchOfflineDraftOrder,
    restaurantUUID,
    selectableFulfillmentOptions,
    showOrderNotes,
    supplierUUID,
    orderDomains,
  } = useNewOrderContext();
  const {
    supplierEmployeeRole,
    restaurantRelationships,
    selectedRestaurantRelationship,
  } = useAuthenticatedUserContext();
  const {
    additionalInputValueMap,
    clearCachedOrder,
    setAdditionalInputValue,
    setNextFulfillment,
    setOrderPaymentMethod,
    selectFulfillment,
    selectedFulfillment,
    submitOrder,
    updateAndSetQuantity,
    updateFulfillment,
  } = useNewOrderActionsContext();
  const { isUpdatingFulfillment, isUpdatingQuantity } =
    useNewOrderLoadingContext();
  const { currencyCode } = useVariantPackInfoContext();
  const { showErrorToast, showToast } = useToasts();
  const adaptOrderItemToItemListOption = useAdaptOrderItemToItemListOption();
  const showOrderAlreadyPlacedToast = useOrderAlreadyPlacedToast();
  const isOfflineModeActive = useOfflineModeActive();
  const { offlineOrderContext } = useOfflineOrderContext(
    restaurantUUID!,
    supplierUUID!,
  );
  const { queueOfflineOrder } = useOfflineQueuedOrders();
  const { fetchOfflinePriceEdits } = useOfflinePriceEdits();
  const orderTotalPrice = useGetOfflineTotalPrice(
    draftOrder?.orders_items ?? [],
  );

  const itemRecommendationsEnabled = useDynamicConfigValue(
    'item_recommendations_enabled',
  );
  const { itemRecommendations } = useItemRecommendationsContext();
  const orderSummarySectionsEnabled = useDynamicConfigValue(
    'order_summary_sections_enabled',
  );
  const offlineModeConfig = useDynamicConfigValue('offline_mode_config');
  const orderSummaryConfig = useDynamicConfigValue('order_summary_config');
  const additionalOrderValidationRolesConfig = useSupplierDynamicConfigValue(
    'additional_order_validation_supplier_roles',
  );

  const essentialItemErrorCalled = useRef(false);
  const initialValidationRef = useRef(true);
  const isUpdatingCartRef = useRef(false);
  // Set to true when opening the line-item note editor so the next focus-triggered
  // validation is skipped when the modal closes (save or dismiss — cart unchanged on dismiss).
  const skipNextFocusValidationRef = useRef(false);
  const listRef = useRef<RecyclerListRef<ItemListOption> | null>(null);
  const orderSubmittedRef = useRef(false);
  const retryUpdateCartQuantitiesRef = useRef<QuantityOverrides>(null);
  const unactionedSubVPsBeforeEssentialItems = useRef<Set<string>>(null);
  const updatedDateAttemptedForRef = useRef<string | undefined>(undefined);

  const [additionalInputErrorMap, setAdditionalInputErrorMap] =
    useState<InputErrorMap>({});
  const [data, setData] = useState<OrderSummary_ValidateOrderMutation>();
  const [hasResponse, setHasResponse] = useState(false);
  const [isSubmittingOfflineOrder, setIsSubmittingOfflineOrder] =
    useState(false);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const [orderInputsDisabled, setOrderInputsDisabled] = useState(false);
  const [isFallbackSheetOpen, setFallbackSheetOpen] = useState(false);
  const [isSameDaySheetOpen, setSameDaySheetOpen] = useState(false);
  const [isSubstitutionSheetOpen, setSubstitutionSheetOpen] = useState(false);
  const [isTimeslotActionSheetOpen, setTimeslotActionSheetOpen] =
    useState(false);
  const [showFulfillmentSelector, setShowFulfillmentSelector] = useState(false);
  const [keyboardExtraSpacing, setKeyboardExtraSpacing] = useState(0);
  const [isUpdateQuantityPending, setIsUpdateQuantityPending] = useState(false);
  const [isCartContentLoading, setIsCartContentLoading] = useState(false);
  const [isEssentialSheetOpen, setEssentialSheetOpen] = useState(false);
  const [missingEssentialItems, setMissingEssentialItems] = useState<string[]>(
    [],
  );
  const isFallbackConfirmed = useRef(false);
  const isSameDayConfirmed = useRef(false);
  const isSubstitutionConfirmed = useRef(false);
  const isEssentialItemSheetConfirmed = useRef(false);

  const isRestaurantOnHold = selectedRestaurantRelationship.onHold;
  const payUponOrdering = selectedRestaurantRelationship.payUponOrdering;
  const orderQueuingDisabled = Boolean(
    isOfflineModeActive && offlineModeConfig?.order_queuing_disabled,
  );

  const {
    containerRef,
    listContainerPaddingBottom,
    onContainerLayout,
    onPageFooterLayout,
    showPageFooter,
  } = useOrderSummaryListLayout();

  const draftOrderUUID = draftOrder?.uuid;
  const lastValidatedOrderUUIDRef = useRef(draftOrderUUID);
  const requiresReview =
    !supplierEmployeeRole &&
    selectedRestaurantRelationship.orderReviewRequired &&
    selectedRestaurantRelationship.employeeRole === EmployeeRole.User;

  const [validateOrder, { loading: isValidating }] = useValidateOrder({
    orderDomains,
    restaurantUUID: restaurantUUID!,
    supplierUUID: supplierUUID!,
  });

  const offlineOrderData = useOfflineDraftOrderToOrderData(
    selectedFulfillment?.date,
    draftOrder,
    restaurantUUID!,
    supplierUUID!,
  );

  const submitQueueSmartOrders = useOrderInboundsStore(
    (state) => state.actions.submitQueueSmartOrders,
  );
  const takePendingReviewTiming = useOrderInboundsStore(
    (state) => state.actions.takePendingReviewTiming,
  );
  const completeInboundReview = useCompleteInboundReview();

  const { isGrossUpTransactionFee, transactionFeeMap } = useTransactionFees(
    restaurantUUID!,
    supplierUUID!,
  );

  // prevent essential items from changing while the sheet is open
  useEffect(() => {
    if (
      !isEssentialSheetOpen &&
      data?.validateOrder.missing_essential_item_ids
    ) {
      setMissingEssentialItems(data.validateOrder.missing_essential_item_ids);
    }
  }, [data, isEssentialSheetOpen]);

  const verifyOrderStatus = useCallback(
    async (orderStatus: OrderStatus | null | undefined) => {
      // Order status is unknown (network or request error) - preserve draft order
      if (orderStatus === undefined) {
        showErrorToast();
        navigateBack();
        return;
      }

      // Order is still in a valid unplaced state
      if (orderStatus && UNPLACED_ORDER_STATUSES.includes(orderStatus)) {
        return;
      }

      // Order was already placed or no longer exists - clear and navigate back
      if (orderStatus) {
        showOrderAlreadyPlacedToast({ source: 'verify_order' });
      } else if (initialValidationRef.current) {
        showErrorToast();
      }

      await clearCachedOrder();
      navigateBack();
    },
    [
      clearCachedOrder,
      navigateBack,
      showErrorToast,
      showOrderAlreadyPlacedToast,
    ],
  );

  const validateOrderCallback = useCallback(
    async (orderUUIDOverride?: string) => {
      const targetOrderUUID = orderUUIDOverride ?? draftOrderUUID;
      if (multiCartEnabled && !targetOrderUUID) {
        initialValidationRef.current = false;
        setHasResponse(true);
        setIsCartContentLoading(false);
        return;
      }

      let orderStatus: OrderStatus | null | undefined;

      try {
        if (!targetOrderUUID || !restaurantUUID) {
          throw new Error('Invalid draft order or restaurant');
        }

        // Smart-order flow needs ascending order so the summary list matches
        // the inbound list, the ERP export, and the order detail view (all
        // oldest-first). Non-smart orders keep the operator facing newest-first order.
        const response = await validateOrder({
          variables: {
            locale: itemQueryVariables.locale,
            ordersItemsOrderBy: [
              { added_at: smartOrderUUID ? Order_By.Asc : Order_By.Desc },
              { restaurant_display_name: Order_By.AscNullsLast },
            ],
            orderUUID: targetOrderUUID,
            restaurantUUID: restaurantUUID!,
            skipSaltApi: itemQueryVariables.skipSaltApi,
            smartOrderUUID: smartOrderUUID || undefined,
          },
        });

        if (!response.data) {
          throw new Error('Invalid validateOrder response');
        }

        orderStatus =
          (response.data?.validateOrder.order?.status as OrderStatus) || null;

        if (
          multiCartEnabled &&
          targetOrderUUID !== lastValidatedOrderUUIDRef.current
        ) {
          return;
        }

        setData(response.data);
      } catch (e) {
        if (initialValidationRef.current) {
          captureException(e, {
            extra: {
              draftOrderUUID,
              orderStatus,
              restaurantUUID,
            },
          });
        }
      }

      if (
        multiCartEnabled &&
        targetOrderUUID !== lastValidatedOrderUUIDRef.current
      ) {
        return;
      }

      clearInitialSubstitutionChoice();
      setIsCartContentLoading(false);
      await verifyOrderStatus(orderStatus);

      initialValidationRef.current = false;
    },
    [
      clearInitialSubstitutionChoice,
      draftOrderUUID,
      itemQueryVariables,
      multiCartEnabled,
      restaurantUUID,
      smartOrderUUID,
      validateOrder,
      verifyOrderStatus,
    ],
  );

  useEffect(() => {
    if (!hasFocus) {
      return;
    }

    if (skipNextFocusValidationRef.current) {
      skipNextFocusValidationRef.current = false;
      return;
    }

    // Defer until all in-flight animations finish to prevent Android crashes.
    // Prevent InteractionManager from hanging indefinitely by also setting a
    // fallback timeout.
    let didValidate = false;
    const runValidation = (firedBy: 'interaction' | 'timeout') => {
      if (didValidate) {
        return;
      }
      didValidate = true;
      clearTimeout(fallbackTimeout);
      task.cancel();

      validateOrderCallback();

      // Add event to track how often InteractionHandler prevents validation
      // for more than VALIDATION_INTERACTION_TIMEOUT_MS
      if (firedBy === 'timeout') {
        Analytics.track('order_summary.validation_fallback_timeout', {
          orderUUID: draftOrderUUID,
        });
      }
    };

    const task = InteractionManager.runAfterInteractions(() =>
      runValidation('interaction'),
    );
    const fallbackTimeout = setTimeout(
      () => runValidation('timeout'),
      VALIDATION_INTERACTION_TIMEOUT_MS,
    );

    return () => {
      task.cancel();
      clearTimeout(fallbackTimeout);
    };
    // Exclude validateOrderCallback from dependencies and only run this effect
    // when the component receives focus, otherwise it may cause validation to
    // run and prematurely close the flow
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFocus]);

  const onOrderItemNotesSaved = useCallback(
    (updatedOrder: NewOrder_OrderFieldsFragment) => {
      setData((prev) =>
        mergeOrderNotesIntoValidateOrderData(prev, updatedOrder),
      );
    },
    [],
  );

  const editOrderItemNoteWithSkipNextFocusValidation = useCallback(
    (params: EditOrderItemNoteParams) => {
      skipNextFocusValidationRef.current = true;
      editOrderItemNote(params);
    },
    [editOrderItemNote],
  );

  // When the order summary is rendered, if the user is in offline mode,
  // and requires pre-auth to place an order, show an error toast explainaing
  // why they're unable to queue an offline order
  useEffect(
    () => {
      if (isOfflineModeActive && payUponOrdering) {
        showErrorToast(i18n.t('order_summary.offline_order_queueing_blocked'));
      }
    },
    // Only want to run this hook on the first render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const { setItemSubstitution, loading: setSubsLoading } =
    useSetItemSubstitution();

  const [clearItemSubstitution] = useMutation<
    Substitutions_ClearItemSubstitutionMutation,
    Substitutions_ClearItemSubstitutionMutationVariables
  >(CLEAR_ITEM_SUBSTITUTION_MUTATION);

  const onClearSubstitutionPress = useCallback(
    async (
      originalVariantPack: OriginalVariantPack,
      substituteVariantPackUUID?: string,
    ) => {
      try {
        if (substituteVariantPackUUID) {
          // update local state to 0 to update prior to mutation
          dispatchQuantityUpdateAction({
            payload: {
              eventPayloads: null,
              newQuantity: 0,
              orderUUID: draftOrderUUID,
              previousQuantity: 1,
              source: 'substitution_clear',
              // item will be removed, so we don't actually need to update the variantPackDetails
              variantPackDetails: {
                itemDisplayName: '',
                metadata: {},
              },
              variantPackUUID: substituteVariantPackUUID,
            },
          });
        }
        await clearItemSubstitution({
          variables: {
            orderUUID: draftOrderUUID!,
            sourceVariantPackUUID: originalVariantPack.uuid,
          },
        });
        await validateOrderCallback();
        const vpsToClear = [originalVariantPack.uuid];
        if (substituteVariantPackUUID) {
          vpsToClear.push(substituteVariantPackUUID);
        }
        // clear VP out of local state in case it gets added back
        dispatchQuantityReducerAction({
          payload: {
            variantPackUUIDs: vpsToClear,
          },
          type: OrderQuantityActions.CLEAR,
        });
        isSubstitutionConfirmed.current = false;
      } catch (e) {
        showErrorToast();
      }
    },
    [
      clearItemSubstitution,
      dispatchQuantityReducerAction,
      dispatchQuantityUpdateAction,
      draftOrderUUID,
      showErrorToast,
      validateOrderCallback,
    ],
  );

  const onDoNotSubstitutePress = useCallback(
    async (originalVariantPack: OriginalVariantPack) => {
      try {
        await setItemSubstitution({
          action: SubstitutionAction.DO_NOT_REPLACE,
          orderUUID: draftOrderUUID!,
          originalVariantPack,
        });
        await validateOrderCallback();
      } catch (e) {
        showErrorToast();
      }
    },
    [
      setItemSubstitution,
      showErrorToast,
      validateOrderCallback,
      draftOrderUUID,
    ],
  );

  const order = data?.validateOrder.order;
  const orderItems = useMemo(() => order?.orders_items || [], [order]);
  const orderFees: OrderFee[] = useMemo(() => {
    if (!order?.orders_items.length) {
      return [];
    }
    return data?.validateOrder.order_fees || [];
  }, [data?.validateOrder.order_fees, order?.orders_items.length]);

  const loyalty = data?.validateOrder.loyalty ?? [];

  const availableQuantityMap = useMemo(() => {
    const initialMap = (
      initialSubstitutionChoice?.substitutionChoices || []
    ).reduce<{ [variantPackUUID: string]: number }>((acc, item) => {
      if (typeof item.availableQuantity === 'number') {
        acc[item.itemListOption.metadata!.variantPackUUID] =
          item.availableQuantity;
      }
      return acc;
    }, {});

    return orderItems.reduce<{
      [variantPackUUID: string]: number;
    }>((result, oi) => {
      const { variants_pack, substitution } = oi;
      const typedSub = substitution as SubstitutionType | null;
      if (
        variants_pack &&
        typeof typedSub?.order_item_available_quantity === 'number'
      ) {
        result[variants_pack.uuid] = typedSub.order_item_available_quantity;
      }
      return result;
    }, initialMap);
  }, [orderItems, initialSubstitutionChoice]);

  const appliedPromotions = useMemo(() => {
    const appliedPromotionsMap = new Map<string, AppliedVolumePromotion>();

    data?.validateOrder.order?.order_promotion_rels.forEach((rel) => {
      if (!appliedPromotionsMap.has(rel.promotion.uuid)) {
        appliedPromotionsMap.set(rel.promotion.uuid, {
          discountInMicros: 0,
          promotion: rel.promotion,
        });
      }

      appliedPromotionsMap.get(rel.promotion.uuid)!.discountInMicros +=
        rel.discount_in_micros ?? 0;
    });

    // Rebates repeat per item with no discount, so badge only the first
    for (const orderItem of data?.validateOrder.order?.orders_items ?? []) {
      const rebate = orderItem.order_item_promotion_rels.find(
        (rel) => rel.promotion.discount_type === PromotionDiscountType.Rebate,
      );

      if (rebate) {
        appliedPromotionsMap.set(rebate.promotion.uuid, {
          discountInMicros: 0,
          promotion: rebate.promotion,
        });
      }
    }

    return Array.from(appliedPromotionsMap.values());
  }, [data?.validateOrder.order]);

  const itemPromotionsMap = useMemo(() => {
    // Create a map of order items -> promotions data by aggregating item-level
    // promotions for each item. Used to render the discount amount and
    // promotion labels in each order item row
    return data?.validateOrder.order?.orders_items.reduce<{
      [orderItemUUID: string]: OrderSummaryItemPromotions;
    }>((result, orderItem) => {
      const orderItemUUID = orderItem.uuid;
      for (const rel of orderItem.order_item_promotion_rels) {
        if (!result[orderItemUUID]) {
          result[orderItemUUID] = {
            discountInMicros: 0,
            promotionLabels: [],
          };
        }

        result[orderItemUUID].discountInMicros += rel.discount_in_micros ?? 0;

        if (
          rel.promotion.discount_type === PromotionDiscountType.Promotion ||
          // Ensure rebate labels have a tagline, otherwise the fallback text would
          // imply that there's a discount
          (rel.promotion.discount_type === PromotionDiscountType.Rebate &&
            rel.promotion.tagline)
        ) {
          result[orderItemUUID].promotionLabels.push({
            expiresAt: rel.promotion.expires_at,
            tagline: rel.promotion.tagline,
            uuid: rel.promotion.uuid,
          });
        }
      }

      return result;
    }, {});
  }, [data?.validateOrder.order?.orders_items]);

  const loyaltyPointsMap = useMemo(() => {
    return (data?.validateOrder.loyalty ?? [])
      .filter((program) => program.enrollment_status === 'ACCEPTED')
      .reduce<{
        [orderItemUUID: string]: number;
      }>((result, program) => {
        for (const item of program.items) {
          result[item.order_item_id] =
            (result[item.order_item_id] ?? 0) + item.estimated_points;
        }

        return result;
      }, {});
  }, [data?.validateOrder.loyalty]);

  const orderFulfillmentType =
    (order?.fulfillment_type as FulfillmentName | undefined) ||
    FulfillmentName.Delivery;
  const timeslotStart = order?.fulfillment_timeslot_start;
  const timeslotEnd = order?.fulfillment_timeslot_end;
  const fulfillmentTimeslot = useMemo<Timeslot | null>(() => {
    return timeslotStart && timeslotEnd
      ? {
          endTime: timeslotEnd,
          startTime: timeslotStart,
        }
      : null;
  }, [timeslotEnd, timeslotStart]);

  const desiredFulfillmentTime = order?.restaurant_desired_delivery_time;

  const orderAvailability = useSupplierOrderAvailability();
  const { variantPackHasLeadTimePolicy } = useItemLeadTimeContext();

  const formattedDate = desiredFulfillmentTime
    ? formatSummaryDate(new Date(desiredFulfillmentTime))
    : '';

  useEffect(() => {
    const updateDateIfInvalid = async () => {
      if (updatedDateAttemptedForRef.current === order?.uuid) {
        return;
      }

      if (order) {
        const savedFulfillmentOption =
          orderAvailability.availabilities.find(
            (availability) =>
              availability.fulfillment_name === orderFulfillmentType,
          ) ?? orderAvailability.selectedAvailability;
        const validFulfillment =
          order.supplier.supplier_metadata && savedFulfillmentOption
            ? getValidFulfillment({
                additionalOverrideSupplierRoles:
                  additionalOrderValidationRolesConfig ?? [],
                availabilities: orderAvailability.availabilities,
                hasLeadTimeItem: cartHasLeadTimeItem(
                  orderItems,
                  variantPackHasLeadTimePolicy,
                ),
                savedFulfillmentDateMoment:
                  order.restaurant_desired_delivery_time
                    ? moment(order.restaurant_desired_delivery_time)
                    : null,
                savedFulfillmentOption,
                supplierEmployeeRole,
                timezones: orderAvailability.timezones,
              })
            : null;
        const isUnsetOrInvalid =
          !order.restaurant_desired_delivery_time ||
          (validFulfillment !== null &&
            isFulfillmentStale({
              savedFulfillmentDate: order.restaurant_desired_delivery_time,
              savedFulfillmentType: savedFulfillmentOption?.fulfillment_name,
              validFulfillment,
            }));

        // do not save if about to navigate back anyway
        const hasItems = orderItems.length > 0;

        if (isUnsetOrInvalid && hasItems && order.uuid === draftOrderUUID) {
          try {
            updatedDateAttemptedForRef.current = order.uuid;
            const nextDateMoment =
              validFulfillment?.date ??
              orderAvailability.nextFulfillmentDayMoment;
            const nextDate = nextDateMoment.toISOString();
            const nextFulfillmentType =
              validFulfillment?.fulfillmentOption.fulfillment_name ??
              orderFulfillmentType;
            await updateFulfillment({
              ...(nextFulfillmentType === order.fulfillment_type
                ? {}
                : { fulfillment_type: nextFulfillmentType }),
              restaurant_desired_delivery_time: nextDate,
            });
            selectFulfillment({
              date: nextDate,
              fulfillmentSettingsUUID:
                validFulfillment?.fulfillmentOption.fulfillment_settings_id ??
                orderAvailability.selectedFulfillmentSettingsUUID,
              timeslot: fulfillmentTimeslot,
              type: nextFulfillmentType,
            });
            await validateOrderCallback();
            const resolvedFallbackOption = validFulfillment?.fulfillmentOption
              .primary_fulfillment_settings_id
              ? validFulfillment.fulfillmentOption
              : null;
            showToast({
              content: resolvedFallbackOption ? (
                <View style={styles.fallbackToast}>
                  <View style={styles.fallbackToastIcon}>
                    <AlertIcon
                      fill={theme.colors.white}
                      height={20}
                      width={20}
                    />
                  </View>
                  <View style={styles.fallbackToastContent}>
                    <Text style={TYPOGRAPHY.toast}>
                      {translateFulfillmentType(
                        resolvedFallbackOption.fulfillment_name,
                        'fallback_notice.title',
                        resolvedFallbackOption.fulfillment_display_name,
                      )}
                    </Text>
                    <Text
                      style={[TYPOGRAPHY.toast, styles.fallbackToastMessage]}
                    >
                      {i18n.t('fallback_notice.message')}
                    </Text>
                  </View>
                </View>
              ) : (
                i18n.t('toast.fulfillment_date_updated', {
                  date: formatSummaryDate(nextDateMoment),
                })
              ),
              type: Type.WARNING,
            });
          } catch (e) {
            showErrorToast();
          }
        }

        setHasResponse(true);
      }
    };

    if (!orderSubmittedRef.current) {
      updateDateIfInvalid();
    }
  }, [
    additionalOrderValidationRolesConfig,
    draftOrderUUID,
    fulfillmentTimeslot,
    order,
    orderAvailability,
    orderFulfillmentType,
    orderItems,
    selectFulfillment,
    showErrorToast,
    showToast,
    styles,
    supplierEmployeeRole,
    theme.colors.white,
    updateFulfillment,
    validateOrderCallback,
    variantPackHasLeadTimePolicy,
  ]);

  const sendOrderToBackend = useCallback(async () => {
    try {
      const matchingOrder = offlineOrderContext.data?.getOrders.find(
        (o) => o.order?.uuid === draftOrderUUID,
      );
      if (isOfflineModeActive && matchingOrder) {
        setCloseable(false);
        setIsSubmittingOfflineOrder(true);
        // Take a snapshot of the price edits at the time of order submission
        const variantPacksPriceEditMapForChat = await fetchOfflinePriceEdits(
          restaurantUUID!,
          supplierUUID!,
        );
        await queueOfflineOrder(
          selectedFulfillment?.date,
          restaurantUUID!,
          supplierUUID!,
          matchingOrder.order!,
          orderTotalPrice,
          additionalInputValueMap,
          variantPacksPriceEditMapForChat,
        );
        await refetchOfflineDraftOrder();
        setIsSubmittingOfflineOrder(false);
        const pendingReviewTiming = smartOrderUUID
          ? takePendingReviewTiming(smartOrderUUID)
          : undefined;
        if (pendingReviewTiming) {
          completeInboundReview(pendingReviewTiming, performance.now());
        }
        setCloseable(true);
        return onOrderPlaced(null);
      }

      setCloseable(false);
      setIsSubmittingOrder(true);
      setOrderInputsDisabled(true);
      const {
        loyaltyEarns,
        order: submittedOrder,
        punchoutOrderMessage,
        splitOrderUUIDs,
      } = await submitOrder(smartOrderUUID);

      if (punchoutOrderMessage) {
        // Send without returning so the order confirmation appears
        postPunchoutOrderMessage(punchoutOrderMessage);
      }

      const invoice = submittedOrder?.order_invoices[0];
      const unpaidInvoice: InvoiceUnpaidItemType | undefined = invoice
        ? {
            amountInMicros: invoice.balance_in_micros ?? 0,
            dateText: null,
            displayNumber: invoice.invoice_number!,
            dueAt: invoice.due_at ? new Date(invoice.due_at) : null,
            orderFulfillmentAt: null,
            orderUUID: invoice.order_uuid!,
            type: PaymentItemType.Invoice,
            uuid: invoice.uuid,
          }
        : undefined;
      const isSplitOrder = splitOrderUUIDs.length > 0;
      orderSubmittedRef.current = true;
      if (
        submittedOrder &&
        UNPLACED_ORDER_STATUSES.includes(submittedOrder.status as OrderStatus)
      ) {
        setOrderInputsDisabled(false);
      }

      // Any smart orders that were part of this order should be flagged as submitted
      submitQueueSmartOrders();
      const pendingReviewTiming = smartOrderUUID
        ? takePendingReviewTiming(smartOrderUUID)
        : undefined;
      if (pendingReviewTiming) {
        completeInboundReview(pendingReviewTiming, performance.now());
      }
      setCloseable(true);

      return onOrderPlaced({
        completionToast:
          isSplitOrder && submittedOrder?.status === OrderStatus.Draft
            ? {
                content: i18n.t('toast.split_order_minimum_not_met'),
              }
            : undefined,
        externalReviewRequired:
          submittedOrder?.status === OrderStatus.InExternalReview,
        fulfillmentDates: getOrderFulfillmentDates(
          orderItems,
          desiredFulfillmentTime,
        ),
        isShippingOrder: FULFILLMENT_SHIPPING_TYPES.includes(
          submittedOrder?.fulfillment_type as FulfillmentName,
        ),
        isSplitOrder,
        loyaltyEarns,
        reviewRequired: submittedOrder?.status === OrderStatus.InReview,
        unpaidInvoice,
      });
    } catch (e) {
      setCloseable(true);
      setOrderInputsDisabled(false);
      if (
        getCodeFromGQLError(e) === OrderValidationErrorCode.ORDER_ALREADY_PLACED
      ) {
        showOrderAlreadyPlacedToast({ source: 'submit_order' });
        await clearCachedOrder();
        navigateBack();
      } else if (
        getCodeFromGQLError(e) === OrderValidationErrorCode.ORDER_FAILED_PAYMENT
      ) {
        showErrorToast(getMessageFromGQLError(e as ApolloError));
      } else {
        showErrorToast();
      }
    } finally {
      setIsSubmittingOrder(false);
    }
  }, [
    additionalInputValueMap,
    clearCachedOrder,
    completeInboundReview,
    desiredFulfillmentTime,
    draftOrderUUID,
    fetchOfflinePriceEdits,
    isOfflineModeActive,
    navigateBack,
    offlineOrderContext,
    onOrderPlaced,
    orderItems,
    orderTotalPrice,
    queueOfflineOrder,
    refetchOfflineDraftOrder,
    restaurantUUID,
    selectedFulfillment,
    setCloseable,
    showErrorToast,
    showOrderAlreadyPlacedToast,
    smartOrderUUID,
    submitOrder,
    submitQueueSmartOrders,
    supplierUUID,
    takePendingReviewTiming,
  ]);

  const localUpdateAndClear = useCallback(
    async (
      pendingQuantities: QuantityOverrides = {},
      scheduledForOrderUUID?: string,
    ) => {
      if (
        multiCartEnabled &&
        scheduledForOrderUUID &&
        scheduledForOrderUUID !== draftOrderUUID
      ) {
        setIsUpdateQuantityPending(false);
        return;
      }
      if (isUpdatingCartRef.current) {
        // If the cart is already being updated, merge pending quantities with any existing ones
        retryUpdateCartQuantitiesRef.current = {
          ...retryUpdateCartQuantitiesRef.current,
          ...pendingQuantities,
        };
        return;
      }

      isUpdatingCartRef.current = true;
      try {
        const result = await updateAndSetQuantity(
          'order_summary',
          pendingQuantities,
        );
        if (result.status === 'no_changes') {
          setIsUpdateQuantityPending(false);
          return;
        }
        if (result.status === 'failed' || !result.order) {
          navigateBack();
          return;
        } else {
          await validateOrderCallback(result.order.uuid);
          setIsUpdateQuantityPending(false);
        }
      } catch (e) {
        showErrorToast();
        setIsUpdateQuantityPending(false);
      } finally {
        isUpdatingCartRef.current = false;
      }
    },
    [
      draftOrderUUID,
      multiCartEnabled,
      navigateBack,
      showErrorToast,
      updateAndSetQuantity,
      validateOrderCallback,
    ],
  );

  const debouncedUpdateAndClear = useDebouncedCallback(
    localUpdateAndClear,
    isOfflineModeActive
      ? OFFLINE_UPDATE_QUANTITY_DEBOUNCE_DELAY_MS
      : UPDATE_CART_DEBOUNCE_DELAY_MS,
  );

  const resetCartContent = useCallback(() => {
    debouncedUpdateAndClear.cancel();
    retryUpdateCartQuantitiesRef.current = null;
    setIsUpdateQuantityPending(false);
    setData(undefined);
    setIsCartContentLoading(true);
    isFallbackConfirmed.current = false;
    isSameDayConfirmed.current = false;
    isSubstitutionConfirmed.current = false;
    isEssentialItemSheetConfirmed.current = false;
    setMissingEssentialItems([]);
    setAdditionalInputErrorMap({});
    void validateOrderCallback();
  }, [debouncedUpdateAndClear, validateOrderCallback]);

  // editCart already synced the context selection, but the summary still holds the pre-edit date
  const handleCartSaved = useCallback(
    (savedFulfillment: SelectedFulfillmentType | null) => {
      if (
        savedFulfillment &&
        editingCart?.orderUUID &&
        editingCart.orderUUID === draftOrderUUID
      ) {
        void validateOrderCallback();
      }
      completeCartFlow();
    },
    [
      completeCartFlow,
      draftOrderUUID,
      editingCart?.orderUUID,
      validateOrderCallback,
    ],
  );

  const lastValidatedCartRef = useRef(activeOrderUUID);
  useEffect(() => {
    if (
      !multiCartEnabled ||
      draftOrderUUID === lastValidatedOrderUUIDRef.current
    ) {
      return;
    }
    const activeCartChanged = activeOrderUUID !== lastValidatedCartRef.current;
    lastValidatedCartRef.current = activeOrderUUID;
    const previousValidatedUUID = lastValidatedOrderUUIDRef.current;
    lastValidatedOrderUUIDRef.current = draftOrderUUID;
    if (!activeCartChanged && previousValidatedUUID === undefined) {
      void validateOrderCallback();
      return;
    }
    resetCartContent();
  }, [
    activeOrderUUID,
    draftOrderUUID,
    multiCartEnabled,
    resetCartContent,
    validateOrderCallback,
  ]);

  useEffect(() => {
    if (!isUpdateQuantityPending && retryUpdateCartQuantitiesRef.current) {
      // Retry any cart updates that were queued while the cart was being updated
      debouncedUpdateAndClear(
        {
          // Shallow copy the pending quantities since the ref is set to null after the update
          ...retryUpdateCartQuantitiesRef.current,
        },
        draftOrderUUID,
      );

      retryUpdateCartQuantitiesRef.current = null;
    }
  }, [debouncedUpdateAndClear, draftOrderUUID, isUpdateQuantityPending]);

  const onSaveFulfillment = useCallback(
    async (
      nextSelectedFulfillment: SelectedFulfillmentType,
      { skipRevalidation = false } = {},
    ): Promise<boolean> => {
      try {
        await setNextFulfillment(nextSelectedFulfillment);
      } catch (e) {
        captureException(e);
        showErrorToast();
        navigateBack();
        return false;
      }

      if (!skipRevalidation) {
        validateOrderCallback();
      }

      return true;
    },
    [navigateBack, setNextFulfillment, showErrorToast, validateOrderCallback],
  );

  const onOrderSummaryVisible = useCallback(() => {
    PerfTiming.end(PerfTimingEvent.ORDER_SUMMARY_VISIBLE, {
      orderUUID: draftOrderUUID!,
      restaurantUUID: selectedRestaurantRelationship.uuid,
      smartOrderUUID,
      supplierUUID: selectedRestaurantRelationship.supplierUUID,
    });

    setFeatureSpanAttributes(Feature.OrderSummary, {
      [SpanAttribute.DraftOrderItemCount]: orderItems.length,
      ...(draftOrderUUID && {
        [SpanAttribute.DraftOrderUUID]: draftOrderUUID,
      }),
      ...(smartOrderUUID && {
        [SpanAttribute.DraftOrderSmartOrderUUID]: smartOrderUUID,
      }),
    });

    setOrderSummaryVisible(true);
  }, [
    draftOrderUUID,
    orderItems.length,
    selectedRestaurantRelationship.supplierUUID,
    selectedRestaurantRelationship.uuid,
    smartOrderUUID,
  ]);

  const removeItems = useCallback(
    async (variantPackUUIDs: string[]) => {
      debouncedUpdateAndClear.cancel();
      variantPackUUIDs.forEach((variantPackUUID) => {
        dispatchQuantityUpdateAction({
          payload: {
            eventPayloads: null,
            newQuantity: 0,
            orderUUID: draftOrderUUID,
            previousQuantity: 1,
            source: 'order_summary',
            // item will be removed, so we don't actually need to update the variantPackDetails
            variantPackDetails: {
              itemDisplayName: '',
              metadata: {},
            },
            variantPackUUID,
          },
        });
      });

      setIsUpdateQuantityPending(true);
      const clearedQuantities = variantPackUUIDs.reduce<{
        [vpUUID: string]: 0;
      }>((acc, variantPackUUID) => {
        acc[variantPackUUID] = 0;
        return acc;
      }, {});
      await localUpdateAndClear(clearedQuantities);
    },
    [
      debouncedUpdateAndClear,
      dispatchQuantityUpdateAction,
      draftOrderUUID,
      localUpdateAndClear,
    ],
  );

  const { alerts, orderFulfillmentAlertMap } = useMemo(() => {
    const result: {
      alerts: ValidateOrderAlert[];
      orderFulfillmentAlertMap: {
        [fulfillmentDate: string]: ValidateOrderAlert;
      };
    } = {
      alerts: [],
      orderFulfillmentAlertMap: {},
    };

    const alertList = data?.validateOrder.alerts || [];
    for (const alert of alertList) {
      if (alert.order_fulfillment_date) {
        result.orderFulfillmentAlertMap[
          moment(alert.order_fulfillment_date).toISOString()
        ] = alert;
      } else {
        result.alerts.push(alert);
      }
    }

    return result;
  }, [data?.validateOrder.alerts]);

  const requiresPaymentMethodOnFileAlert = alerts.some(
    (alert) => alert.alert_code === 'PAYMENT_METHOD_ON_FILE_REQUIRED',
  );

  const showSplitOrderSections = useMemo(() => {
    if (orderSummarySectionsEnabled) {
      return true;
    }
    const orderMinimum = data?.validateOrder.order_minimum;
    const hasHardOrderMinimumAmount =
      orderMinimum?.is_hard && orderMinimum.unit === OrderConstraintUnit.Amount;
    const hasLeadTimeOrderItems = data?.validateOrder.order?.orders_items.some(
      (oi) => oi.earliest_fulfillment_at,
    );
    const splitBehavior =
      data?.validateOrder.order?.supplier.supplier_metadata
        ?.lead_time_split_behavior;
    return Boolean(
      splitBehavior === LeadTimeSplitBehavior.EnforceOrderMinimum &&
        hasHardOrderMinimumAmount &&
        hasLeadTimeOrderItems,
    );
  }, [data, orderSummarySectionsEnabled]);

  const sections: ListSection<OrderSummaryListItemType>[] = useMemo(() => {
    const adaptGroupToListItem = (
      group: OrderSummary_ItemListFieldsFragment[],
    ): OrderSummaryListItemType => {
      const firstItem = group[0];
      const adaptedFirstItem = adaptOrderItemToItemListOption(firstItem);
      const substitutionVariantPack =
        firstItem.substitution?.original_variant_pack;
      const substitutionAvailableQuantity =
        firstItem.substitution?.order_item_available_quantity;

      if (
        initialSubstitutionChoice &&
        substitutionVariantPack &&
        substitutionVariantPack.uuid ===
          initialSubstitutionChoice.substitutionVariantPackUUID
      ) {
        return {
          metadata: {
            orderItems: initialSubstitutionChoice.substitutionChoices.map(
              (sub) => ({
                item: sub.itemListOption,
                substitution: {
                  action: SubstitutionAction.SUBSTITUTE,
                  order_item_available_quantity: substitutionAvailableQuantity,
                  original_variant_pack: substitutionVariantPack,
                },
              }),
            ),
            substitutionAction: SubstitutionAction.SUBSTITUTE,
            substitutionVariantPack:
              firstItem.substitution.original_variant_pack,
          },
          title: adaptedFirstItem.title,
          value: adaptedFirstItem.value,
        };
      }

      return {
        metadata: {
          orderItems: group.map((oi) => ({
            item: adaptOrderItemToItemListOption(oi),
            substitution: oi.substitution || null,
          })),
          substitutionAction: firstItem.substitution?.action || null,
          substitutionVariantPack:
            firstItem.substitution?.original_variant_pack || null,
        },
        title: adaptedFirstItem.title,
        value: adaptedFirstItem.value,
      };
    };

    const groupedOrderItems =
      groupBySubstitution<OrderSummary_ItemListFieldsFragment>(orderItems);

    if (!showSplitOrderSections) {
      // Return a single section as a flat list of order items
      return [
        {
          data: groupedOrderItems.map(adaptGroupToListItem),
          key: null,
        },
      ];
    }

    const selectedFulfillmentDate = selectedFulfillment
      ? moment(selectedFulfillment.date)
      : null;

    // Group order items into sections where each section represents a split
    // order and the section keys are that order's fulfillment date
    const fulfillmentDateToItemsMap: {
      [date: string]: OrderSummaryListItemType[];
    } = {};
    return (
      groupedOrderItems
        .reduce<ListSection<OrderSummaryListItemType>[]>((acc, group) => {
          const topLevelItem = group[0];
          const isPrebookItem = Boolean(
            topLevelItem.variants_pack.prebook_config,
          );

          // Always use the earliest fulfillment date for prebooked items.
          // Otherwise group items into the later date of either the item's
          // earliest fulfillment date or selected order fulfillment date.
          const earliestFulfillmentAt = isPrebookItem
            ? moment(topLevelItem.earliest_fulfillment_at ?? 0)
            : moment.max(
                moment(topLevelItem.earliest_fulfillment_at ?? 0),
                moment(selectedFulfillmentDate ?? 0),
              );

          const itemFulfillmentDate = earliestFulfillmentAt
            .startOf('day')
            .toISOString();
          if (!fulfillmentDateToItemsMap[itemFulfillmentDate]) {
            fulfillmentDateToItemsMap[itemFulfillmentDate] = [];
            acc.push({
              data: fulfillmentDateToItemsMap[itemFulfillmentDate],
              key: itemFulfillmentDate,
            });
          }

          const summaryListItem = adaptGroupToListItem(group);
          fulfillmentDateToItemsMap[itemFulfillmentDate].push(summaryListItem);
          return acc;
        }, [])
        // Sort sections by the split order's fulfillment date
        .sort((a, b) => a.key!.localeCompare(b.key!))
    );
  }, [
    adaptOrderItemToItemListOption,
    initialSubstitutionChoice,
    orderItems,
    selectedFulfillment,
    showSplitOrderSections,
  ]);

  const openNewCartFromTabs = useCallback(
    () => openNewCart(null),
    [openNewCart],
  );

  const {
    onDisplayTextChange: setSearchTerm,
    clearSearch,
    searchText: searchTerm,
    searching,
    sectionData: filteredSections,
  } = useMultiSearchFilters<OrderSummaryListItemType>({
    data: sections,
    getSearchableFields,
    removeEmptySections: false,
    sectionList: true,
  });

  const showNoSearchResults =
    (!filteredSections.length || filteredSections[0].data.length === 0) &&
    !orderSummarySectionsEnabled &&
    !searching;

  const searchClearedForCartRef = useRef(draftOrderUUID);
  useEffect(() => {
    if (
      !multiCartEnabled ||
      searchClearedForCartRef.current === draftOrderUUID
    ) {
      return;
    }
    searchClearedForCartRef.current = draftOrderUUID;
    clearSearch();
  }, [clearSearch, draftOrderUUID, multiCartEnabled]);

  const sectionMetadataMap = useMemo(() => {
    if (!showSplitOrderSections) {
      return {};
    }

    const variantPackToOrderItem = orderItems.reduce<{
      [variantPackUUID: string]:
        | OrderSummary_ItemListFieldsFragment
        | undefined;
    }>((acc, oi) => {
      acc[oi.variants_pack.uuid] = oi;
      return acc;
    }, {});

    const selectedFulfillmentSectionKey = sections.find((s) =>
      moment(s.key).isSame(selectedFulfillment?.date, 'day'),
    )?.key;

    return sections.reduce<SectionMetadataMap>((acc, section, sectionIndex) => {
      if (section.key) {
        const {
          baseAmountInMicros: finalOrderTotalWithoutPromos,
          discountedAmountInMicros: finalOrderTotalWithPromos,
        } = section.data.reduce<TotalPricesMap>(
          (sums, orderItem) => {
            const orderItemMetadata = orderItem.metadata?.orderItems;
            if (orderItemMetadata) {
              const totalOfItems = orderItemMetadata.reduce<TotalPricesMap>(
                (innerAcc, oiData) => {
                  const oiMetadata = oiData.item.metadata;
                  if (!oiMetadata) {
                    return innerAcc;
                  }
                  const vpUUID = oiMetadata.variantPackUUID;
                  const quantity = savedQuantityMap[vpUUID];
                  // Guard against order item being removed before validate updates list of items
                  if (typeof quantity !== 'number') {
                    return innerAcc;
                  }

                  const priceWithPromos =
                    getTotalLineItemPrice({
                      quantity,
                      unitCount: oiMetadata.unitCount,
                      unitPriceInMicros:
                        oiMetadata.promotionPricePerUnitInMicros ??
                        oiMetadata.unitPriceInMicros,
                    }) ?? 0;
                  const priceWithoutPromos =
                    getTotalLineItemPrice({
                      quantity,
                      unitCount: oiMetadata.unitCount,
                      unitPriceInMicros: oiMetadata.unitPriceInMicros,
                    }) ?? 0;

                  return {
                    baseAmountInMicros:
                      innerAcc.baseAmountInMicros + priceWithoutPromos,
                    discountedAmountInMicros:
                      innerAcc.discountedAmountInMicros + priceWithPromos,
                  };
                },
                {
                  baseAmountInMicros: 0,
                  discountedAmountInMicros: 0,
                },
              );

              return {
                baseAmountInMicros:
                  sums.baseAmountInMicros + totalOfItems.baseAmountInMicros,
                discountedAmountInMicros:
                  sums.discountedAmountInMicros +
                  totalOfItems.discountedAmountInMicros,
              };
            }

            return sums;
          },
          {
            baseAmountInMicros: 0,
            discountedAmountInMicros: 0,
          },
        );

        const unitsInOrder = section.data.reduce((sum, orderItem) => {
          const orderItemMetadata = orderItem.metadata?.orderItems;
          if (orderItemMetadata) {
            const totalOfItems = orderItemMetadata.reduce(
              (innerAcc, oiData) => {
                const oiMetadata = oiData.item.metadata;
                if (!oiMetadata) {
                  return innerAcc;
                }
                const vpUUID = oiMetadata.variantPackUUID;
                const foundOrderItem = variantPackToOrderItem[vpUUID];
                const quantityInOrder =
                  foundOrderItem?.order_item_prices?.pack_quantity_at_order ??
                  0;
                return innerAcc + quantityInOrder;
              },
              0,
            );

            return sum + totalOfItems;
          }
          return sum;
        }, 0);

        const sectionHeaderText = data?.validateOrder.section_headers?.find(
          ({ order_fulfillment_date }) =>
            section.key &&
            order_fulfillment_date &&
            new Date(section.key).toISOString() ===
              new Date(order_fulfillment_date).toISOString(),
        )?.header_text;
        // If section_headers is an empty list, we use a default value
        const unitText = i18n.t(
          unitsInOrder === 1 ? 'unit_count' : 'unit_count_plural',
          { count: unitsInOrder },
        );

        acc[section.key] = {
          baseAmountInMicros: finalOrderTotalWithoutPromos,
          discountedAmountInMicros: finalOrderTotalWithPromos,
          hasNoSearchResults: Boolean(
            searchTerm && filteredSections[sectionIndex].data.length === 0,
          ),
          headerText: sectionHeaderText ?? unitText,
          orderMinimumError:
            orderFulfillmentAlertMap[section.key]?.alert_message,
          // When there is a section that matches the selected fulfillment date,
          // render the fulfillment button for only that section. Otherwise, render
          // the fulfillment button for the first section. This can happen when
          // prebook items have an earlier fulfillment date than the rest of the order.
          showFulfillmentButton: selectedFulfillmentSectionKey
            ? section.key === selectedFulfillmentSectionKey
            : sectionIndex === 0,
        };
      }

      return acc;
    }, {});
  }, [
    data?.validateOrder.section_headers,
    filteredSections,
    orderFulfillmentAlertMap,
    orderItems,
    savedQuantityMap,
    searchTerm,
    sections,
    selectedFulfillment?.date,
    showSplitOrderSections,
  ]);

  const orderMinimumErrorCount = useMemo(() => {
    return Object.keys(sectionMetadataMap).filter(
      (key) => sectionMetadataMap[key].orderMinimumError,
    ).length;
  }, [sectionMetadataMap]);

  const placeOrderButtonText = useMemo(() => {
    if (isRestaurantOnHold) {
      return i18n.t('account_on_hold');
    }

    const orderItemCount = showSplitOrderSections
      ? sections.reduce((acc, section) => {
          const canPlaceSection =
            !sectionMetadataMap[section.key!]?.orderMinimumError;
          if (canPlaceSection) {
            return acc + section.data.length;
          }
          return acc;
        }, 0)
      : orderItems.length;

    if (requiresReview) {
      return i18n.t(
        orderItemCount === 1
          ? 'submit_for_review_items_singular'
          : 'submit_for_review_items',
        { orderItemCount },
      );
    }

    const orderCount = sections.length;
    const placeableOrderCount = orderCount - orderMinimumErrorCount;
    if (orderCount > 1 && placeableOrderCount > 0) {
      return i18n.t(
        orderItemCount === 1
          ? isOfflineModeActive
            ? 'queue_order_count_items_singular'
            : 'place_order_count_items_singular'
          : isOfflineModeActive
            ? 'queue_order_count_items'
            : 'place_order_count_items',
        {
          count: placeableOrderCount,
          orderItemCount,
          total: orderCount,
        },
      );
    }

    const canQueueOfflineOrder = isOfflineModeActive && !orderQueuingDisabled;
    return i18n.t(
      orderItemCount === 1
        ? canQueueOfflineOrder
          ? 'queue_order_items_singular'
          : 'place_order_items_singular'
        : canQueueOfflineOrder
          ? 'queue_order_items'
          : 'place_order_items',
      {
        orderItemCount,
      },
    );
  }, [
    isOfflineModeActive,
    isRestaurantOnHold,
    orderItems.length,
    orderMinimumErrorCount,
    orderQueuingDisabled,
    requiresReview,
    sectionMetadataMap,
    sections,
    showSplitOrderSections,
  ]);

  const calculatedTransactionFee = useMemo(() => {
    if (!order || !selectedPaymentMethod || !payUponOrdering) {
      return 0;
    }

    const subtotalFee = orderFees.find(
      (fee) => fee.display_type === OrderFeeDisplayType.Subtotal,
    );
    const subtotalInMicros = subtotalFee?.amount_at_order_micros;

    if (typeof subtotalInMicros !== 'number') {
      return 0;
    }

    const selectedPaymentMethodType =
      selectedPaymentMethod?.type as PaymentMethodType;

    const flatTransactionFee =
      transactionFeeMap.FLAT[selectedPaymentMethodType];

    const percentageTransactionFee =
      transactionFeeMap.PERCENTAGE[selectedPaymentMethodType];

    if (flatTransactionFee) {
      return flatTransactionFee * MICRO_CONVERSION;
    } else if (percentageTransactionFee) {
      // Check if there are promotions applied by comparing discounted vs base amounts
      const totalDiscountedAmount = Object.keys(sectionMetadataMap).reduce(
        (acc, sectionKey) => {
          const metadata = sectionMetadataMap[sectionKey];
          if (!metadata.orderMinimumError) {
            return acc + metadata.discountedAmountInMicros;
          }
          return acc;
        },
        0,
      );

      const totalBaseAmount = Object.keys(sectionMetadataMap).reduce(
        (acc, sectionKey) => {
          const metadata = sectionMetadataMap[sectionKey];
          if (!metadata.orderMinimumError) {
            return acc + metadata.baseAmountInMicros;
          }
          return acc;
        },
        0,
      );

      // If theres a discount (discounted amount < base amount) use discounted amount, otherwise use the original subtotal fee amount
      const hasPromotion =
        totalDiscountedAmount < totalBaseAmount && totalDiscountedAmount > 0;
      const amountForFeeCalculation = hasPromotion
        ? totalDiscountedAmount
        : subtotalInMicros;

      const feePercentageAmount = percentageTransactionFee / 100;
      const feeMultiplier = isGrossUpTransactionFee
        ? feePercentageAmount / (1 - feePercentageAmount)
        : feePercentageAmount;
      return roundMicrosToNearestWholeCent(
        feeMultiplier * amountForFeeCalculation,
      );
    }

    return 0;
  }, [
    isGrossUpTransactionFee,
    order,
    orderFees,
    payUponOrdering,
    selectedPaymentMethod,
    sectionMetadataMap,
    transactionFeeMap,
  ]);

  const totalPrice = useMemo(() => {
    if (
      isUpdateQuantityPending ||
      isValidating ||
      !selectedRestaurantRelationship.displayItemPrices ||
      !order
    ) {
      return displayCurrencyPlaceholder(currencyCode);
    }

    if (isOfflineModeActive && offlineOrderData) {
      return offlineOrderData.orderTotalPrice;
    }

    if (!showSplitOrderSections) {
      // Display total price directly from the order fees view
      return displayTotalPrice(
        {
          orderFees,
          orderItems: order.orders_items,
        },
        { displayItemPrices: selectedRestaurantRelationship.displayItemPrices },
      );
    }

    // Sum split order totals from the section metadata map
    const orderTotalInMicros = Object.keys(sectionMetadataMap).reduce(
      (acc, sectionKey) => {
        const metadata = sectionMetadataMap[sectionKey];
        if (!metadata.orderMinimumError) {
          return acc + metadata.baseAmountInMicros;
        }

        return acc;
      },
      0,
    );

    // Extract fees from the order fees view
    const orderFeesInMicros = orderFees.reduce((acc, fee) => {
      const amountInMicros =
        fee.amount_at_invoice_micros ?? fee.amount_at_order_micros;
      if (
        fee.display_type === OrderFeeDisplayType.Fee &&
        typeof amountInMicros === 'number'
      ) {
        return acc + amountInMicros;
      }

      return acc;
    }, 0);

    const totalWithTransactionFee =
      orderTotalInMicros + orderFeesInMicros + calculatedTransactionFee;
    return formatCurrency(totalWithTransactionFee, currencyCode);
  }, [
    currencyCode,
    isOfflineModeActive,
    isUpdateQuantityPending,
    isValidating,
    offlineOrderData,
    order,
    orderFees,
    sectionMetadataMap,
    selectedRestaurantRelationship.displayItemPrices,
    showSplitOrderSections,
    calculatedTransactionFee,
  ]);

  const subtotal = useMemo(() => {
    if (!selectedRestaurantRelationship.displayItemPrices) {
      return null;
    }
    if (isUpdateQuantityPending || isValidating) {
      return displayCurrencyPlaceholder(currencyCode);
    }

    const subtotalFee = orderFees.find(
      (fee) => fee.display_type === OrderFeeDisplayType.Subtotal,
    );
    const subtotalAmountInMicros = subtotalFee?.amount_at_order_micros;
    return typeof subtotalAmountInMicros === 'number'
      ? formatCurrency(subtotalAmountInMicros, currencyCode)
      : null;
  }, [
    selectedRestaurantRelationship.displayItemPrices,
    isUpdateQuantityPending,
    isValidating,
    orderFees,
    currencyCode,
  ]);

  const showLoadingSpinner =
    isSubmittingOrder ||
    isSubmittingOfflineOrder ||
    isUpdatingFulfillment ||
    isUpdatingQuantity ||
    isUpdateQuantityPending ||
    isValidating ||
    setSubsLoading;

  const additionalInputs: OrderSummaryInput[] = useMemo(() => {
    if (searchTerm) {
      return [];
    }

    const additionalInputsConfig = orderSummaryConfig?.additional_inputs;
    if (additionalInputsConfig) {
      const isSupplierEmployee = Boolean(supplierEmployeeRole);
      return additionalInputsConfig.filter((input) => {
        switch (input.viewable_by) {
          case ViewableBy.ALL:
            return true;
          case ViewableBy.SUPPLIER:
            return isSupplierEmployee;
          case ViewableBy.RESTAURANT:
            return !isSupplierEmployee;
          default:
            /**
             * This applies for the viewable_by = undefined case, that should be treated as ALL.
             * It happens for previously created chat dynamic configs that didn't have the viewable_by field.
             */
            return true;
        }
      });
    }

    const inputs: OrderSummaryInput[] = [];
    if (poNumberDisplay !== PoNumberDisplay.Hide) {
      inputs.push({
        id: OrderSummaryInputID.PONumber,
        pattern: null,
        pattern_invalid_message: null,
        placeholder: null,
        required: poNumberDisplay === PoNumberDisplay.Required,
        subtitle: null,
        title: i18n.t('po_number'),
        type: 'text',
        viewable_by: ViewableBy.ALL,
      });
    }

    if (showOrderNotes) {
      inputs.push({
        id: OrderSummaryInputID.Notes,
        pattern: null,
        pattern_invalid_message: null,
        placeholder: null,
        required: false,
        subtitle: i18n.t('order_notes_description'),
        title: i18n.t('order_notes'),
        type: 'textarea',
        viewable_by: ViewableBy.ALL,
      });
    }

    return inputs;
  }, [
    orderSummaryConfig?.additional_inputs,
    poNumberDisplay,
    searchTerm,
    showOrderNotes,
    supplierEmployeeRole,
  ]);

  const [paymentMethodValidationMessage, setPaymentMethodValidationMessage] =
    useState<string | null>(null);

  useEffect(() => {
    if (payUponOrdering && !loadingPaymentMethod) {
      if (selectedPaymentMethod) {
        if (
          selectedPaymentMethod.type === PaymentMethodType.ACH ||
          selectedPaymentMethod.type === PaymentMethodType.PAYTO ||
          selectedPaymentMethod.type === PaymentMethodType.PLAID_ACH
        ) {
          setPaymentMethodValidationMessage(
            i18n.t('pay_on_order_invalid_method'),
          );
        } else if (
          selectedPaymentMethod.subtype === AUSTRALIA_EFTPOS_SUBTYPE ||
          selectedPaymentMethod.type === PaymentMethodType.TAB
        ) {
          setPaymentMethodValidationMessage(
            i18n.t('pay_on_order_invalid_method.card_type'),
          );
        } else if (
          selectedPaymentMethod.type === PaymentMethodType.CREDIT_CARD ||
          selectedPaymentMethod.type === PaymentMethodType.DEBIT_CARD
        ) {
          setPaymentMethodValidationMessage(null);
          setOrderPaymentMethod(selectedPaymentMethod);
        }
      } else {
        setPaymentMethodValidationMessage(
          i18n.t('pay_on_order_no_method_selected'),
        );
      }
    }
  }, [
    loadingPaymentMethod,
    payUponOrdering,
    selectedPaymentMethod,
    setOrderPaymentMethod,
    setPaymentMethodValidationMessage,
  ]);

  const variantPacksWithUnactionedSubs = useMemo<Set<string>>(() => {
    const orderItemsToCheck = data?.validateOrder?.order?.orders_items || [];
    return orderItemsToCheck.reduce<Set<string>>((acc, oi) => {
      if (oi.substitution && !oi.substitution.action) {
        acc.add(oi.variants_pack.uuid);
      }
      return acc;
    }, new Set());
  }, [data]);

  const firstUnactionedSubIndex = useMemo(() => {
    let currentIndex = 0;
    for (const section of sections) {
      if (typeof section.key === 'string') {
        currentIndex++;
      }
      for (const item of section.data) {
        if (
          item.metadata!.substitutionVariantPack &&
          !item.metadata!.substitutionAction
        ) {
          return currentIndex;
        }
        currentIndex++;
      }
    }
  }, [sections]);

  const [requiresTimeslot, availableTimeslots] = useMemo<
    [boolean, TimeslotOption[]]
  >(() => {
    const availabilityForDate = findAvailabilityDay(
      orderAvailability.selectedAvailability,
      moment(desiredFulfillmentTime!),
    );
    if (!availabilityForDate || !availabilityForDate.timeslot_settings) {
      return [false, []];
    }

    const requireTimeslots = Boolean(
      availabilityForDate?.timeslot_settings?.selection_required,
    );

    const timeslotOptions = orderAvailability.getTimeslotOptionsForDay(
      availabilityForDate,
      supplierEmployeeRole,
      additionalOrderValidationRolesConfig ?? [],
    );
    return [requireTimeslots, timeslotOptions];
  }, [
    desiredFulfillmentTime,
    orderAvailability,
    supplierEmployeeRole,
    additionalOrderValidationRolesConfig,
  ]);

  const selectedFulfillmentOption = useMemo(() => {
    const fulfillmentSettingsUUID =
      selectedFulfillment?.fulfillmentSettingsUUID;

    return (
      (fulfillmentSettingsUUID
        ? availableOrderFulfillments.find(
            (option) =>
              option.fulfillment_settings_id === fulfillmentSettingsUUID,
          )
        : availableOrderFulfillments.find(
            (option) => option.fulfillment_name === selectedFulfillment?.type,
          )) ?? null
    );
  }, [
    availableOrderFulfillments,
    selectedFulfillment?.fulfillmentSettingsUUID,
    selectedFulfillment?.type,
  ]);

  const selectedFallbackOption =
    selectedFulfillmentOption?.primary_fulfillment_settings_id
      ? selectedFulfillmentOption
      : null;

  const checkConfirmationsAndSubmitOrder = useCallback(
    async (skipTimeslot = false) => {
      const nextInputErrorMap: InputErrorMap = {};

      for (const additionalInput of additionalInputs) {
        const inputValue = (
          additionalInputValueMap[additionalInput.id]?.value ?? ''
        ).trim();
        const error = getOrderSummaryInputError(additionalInput, inputValue);
        if (error) {
          nextInputErrorMap[additionalInput.id] = error;
        }
      }

      setAdditionalInputErrorMap(nextInputErrorMap);
      if (Object.keys(nextInputErrorMap).length) {
        return;
      }

      const requiresSameDayConfirmation =
        !selectedFallbackOption &&
        moment(desiredFulfillmentTime!).isSame(moment(getTrueTime()), 'day') &&
        !requiresReview &&
        !orderAvailability.isShippingOption;
      if (selectedFallbackOption && !isFallbackConfirmed.current) {
        setFallbackSheetOpen(true);
      } else if (requiresSameDayConfirmation && !isSameDayConfirmed.current) {
        setSameDaySheetOpen(true);
      } else if (
        !skipTimeslot &&
        requiresTimeslot &&
        !fulfillmentTimeslot &&
        orderAvailability.selectedAvailability &&
        availableTimeslots.length
      ) {
        setTimeslotActionSheetOpen(true);
      } else if (
        typeof firstUnactionedSubIndex === 'number' &&
        !isSubstitutionConfirmed.current
      ) {
        setSubstitutionSheetOpen(true);
      } else if (
        missingEssentialItems.length &&
        !isEssentialItemSheetConfirmed.current
      ) {
        unactionedSubVPsBeforeEssentialItems.current =
          variantPacksWithUnactionedSubs;
        setEssentialSheetOpen(true);
      } else {
        sendOrderToBackend();
      }
    },
    [
      additionalInputs,
      additionalInputValueMap,
      availableTimeslots.length,
      desiredFulfillmentTime,
      selectedFallbackOption,
      firstUnactionedSubIndex,
      fulfillmentTimeslot,
      missingEssentialItems.length,
      orderAvailability,
      requiresReview,
      requiresTimeslot,
      sendOrderToBackend,
      variantPacksWithUnactionedSubs,
    ],
  );

  const sharedSubmitButtonProps: {
    children: string;
    disabled: boolean;
    loading: boolean;
  } = {
    children: placeOrderButtonText,
    disabled:
      orderItems.length === 0 ||
      showLoadingSpinner ||
      isRestaurantOnHold ||
      !data?.validateOrder.can_place_order ||
      !formattedDate ||
      !orderFulfillmentType ||
      paymentMethodValidationMessage !== null ||
      // When pre-auth (pay upon ordering) is enabled, we cannot allow users to queue orders
      // offline, because we need to pre-auth their payment method at time of order
      (isOfflineModeActive && payUponOrdering) ||
      orderQueuingDisabled,
    loading: showLoadingSpinner,
  };

  const unitCount = useMemo(() => {
    return orderItems.reduce((acc, oi) => {
      return acc + (oi.order_item_prices?.pack_quantity_at_order ?? 0);
    }, 0);
  }, [orderItems]);

  const onSubmitEssentialItems = () => {
    if (isUpdateQuantityPending || isValidating) {
      return;
    }

    if (variantPacksWithUnactionedSubs.size) {
      const previousValue = unactionedSubVPsBeforeEssentialItems.current;

      const shouldOpenSubstitutionSheet =
        !previousValue ||
        previousValue.size !== variantPacksWithUnactionedSubs.size ||
        [...variantPacksWithUnactionedSubs].some((vpUUID) => {
          return !previousValue.has(vpUUID);
        });

      if (shouldOpenSubstitutionSheet) {
        // unmark subs as confirmed because new subs added
        isSubstitutionConfirmed.current = false;
        // mark essential items confirmed because it was recently viewed
        isEssentialItemSheetConfirmed.current = true;
        // remove essential item sheet and replace with subs sheet
        setEssentialSheetOpen(false);
        setSubstitutionSheetOpen(true);
        return;
      }
    }

    setEssentialSheetOpen(false);
    sendOrderToBackend();
  };

  const handlePlaceOrderPress = () => {
    if (
      isUpdateQuantityPending ||
      isValidating ||
      sharedSubmitButtonProps.disabled
    ) {
      return;
    }
    checkConfirmationsAndSubmitOrder();
  };

  useKeyboardShortcuts(
    AllowedShortcuts.PlaceOrder,
    handlePlaceOrderPress,
    [
      isUpdateQuantityPending,
      isValidating,
      sharedSubmitButtonProps.disabled,
      checkConfirmationsAndSubmitOrder,
    ],
    {},
    true,
    KeyboardScope.ORDER_SUMMARY,
  );

  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  const sectionsWithFlatIndex = useMemo(() => {
    let itemIndex = 0;
    return filteredSections.map((section) => ({
      ...section,
      data: section.data.map((item) => {
        item.flatIndex = itemIndex++;
        return item;
      }),
    }));
  }, [filteredSections]);

  const flatData = useMemo(
    () => sectionsWithFlatIndex.flatMap((section) => section.data),
    [sectionsWithFlatIndex],
  );

  const centerItemAtIndex = useCallback(
    (index: number) => {
      if (listRef.current && flatData.length > 0) {
        const itemHeight = 64; // TODO(VM): check if this is the correct height
        const halfScreenHeight = windowDimensions.height / 2;
        const offset = index * itemHeight - halfScreenHeight + itemHeight / 2;
        const viewOffset = Math.max(0, offset);

        listRef.current.scrollToIndex({ index, viewOffset });
      }
    },
    [flatData, windowDimensions],
  );

  /**
   * Disabling the SCOPED keyboard shortcuts, to allow for ORDER_SUMMARY and GLOBAL keyboard shortcuts to work
   * This is done to allow for the user to navigate through the order summary using arrow keys
   * and to allow for the user to toggle the cart
   */
  useEffect(() => {
    disableScope(KeyboardScope.SCOPED);
    enableScope(KeyboardScope.ORDER_SUMMARY);
    enableScope(KeyboardScope.GLOBAL);
    return () => {
      disableScope(KeyboardScope.ORDER_SUMMARY);
      disableScope(KeyboardScope.ACTION_SHEET);
      enableScope(KeyboardScope.GLOBAL);
      enableScope(KeyboardScope.SCOPED);
    };
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const focusItemAtIndex = useCallback(
    (index: number) => {
      if (index >= 0 && index < flatData.length) {
        setFocusedIndex(index);
        centerItemAtIndex(index);
      }
    },
    [centerItemAtIndex, flatData],
  );

  const moveFocusUp = useCallback(() => {
    if (focusedIndex === null) {
      focusItemAtIndex(flatData.length - 1);
    } else {
      focusItemAtIndex((focusedIndex - 1 + flatData.length) % flatData.length);
    }
  }, [focusItemAtIndex, focusedIndex, flatData]);

  const moveFocusDown = useCallback(() => {
    if (focusedIndex === null) {
      focusItemAtIndex(0);
    } else {
      focusItemAtIndex((focusedIndex + 1) % flatData.length);
    }
  }, [focusItemAtIndex, focusedIndex, flatData]);

  useKeyboardShortcuts(
    AllowedShortcutNumbers,
    (_, handler) => {
      const key = handler.keys?.join().toLowerCase();
      const numberPressed = numberKeyMap[`${Key.Alt}+${key}`];
      if (numberPressed !== undefined) {
        const index = numberPressed - 1;
        focusItemAtIndex(index);
      }
    },
    [flatData],
    {},
    true,
    KeyboardScope.ORDER_SUMMARY,
  );

  useKeyboardShortcuts(
    AllowedShortcuts.ArrowUp,
    moveFocusUp,
    [moveFocusUp],
    {},
    true,
    KeyboardScope.ORDER_SUMMARY,
  );

  useKeyboardShortcuts(
    AllowedShortcuts.ArrowDown,
    moveFocusDown,
    [moveFocusDown],
    {},
    true,
    KeyboardScope.ORDER_SUMMARY,
  );

  const isOnlineWithoutSearch = !isOfflineModeActive && !searchTerm;
  const showPromotionBadge =
    appliedPromotions.length > 0 && isOnlineWithoutSearch;
  const showRecommendations =
    itemRecommendationsEnabled &&
    itemRecommendations.length > 0 &&
    isOnlineWithoutSearch;
  const showOrderSummaryInputs = Boolean(
    draftOrderUUID && (additionalInputs.length || isOnlineWithoutSearch),
  );
  const orderSummaryInputsNeedsTopSpacing =
    !showPromotionBadge && !showRecommendations;
  const showFulfillmentOnlySubheader = Boolean(
    orderSummarySectionsEnabled && multiCartEnabled && !orderItems.length,
  );
  const fulfillmentDropdown = useMemo(
    () =>
      orderSummarySectionsEnabled && !isSmallLayout && selectedFulfillment ? (
        <FulfillmentPickerDropdown
          draftOrder={draftOrder}
          fulfillmentOptions={selectableFulfillmentOptions}
          onSave={async (newFulfillment) => {
            isSameDayConfirmed.current = false;
            await onSaveFulfillment(newFulfillment);
          }}
          selectedFulfillment={selectedFulfillment}
          trackingProperties={{
            smartOrderUUID,
            source: 'order_summary',
          }}
        />
      ) : null,
    [
      draftOrder,
      selectableFulfillmentOptions,
      isSmallLayout,
      onSaveFulfillment,
      orderSummarySectionsEnabled,
      selectedFulfillment,
      smartOrderUUID,
    ],
  );

  const listHeader = useMemo(
    () => (
      <>
        <OrderSummaryListHeader
          alerts={alerts}
          isSearching={searching}
          multiCartEnabled={multiCartEnabled}
          onNewCartPress={openNewCartFromTabs}
          onSearchChange={setSearchTerm}
          onSeeAllCartsPress={openAllCarts}
          searchTerm={searchTerm}
          showNoResults={showNoSearchResults}
        />
        {showFulfillmentOnlySubheader &&
        desiredFulfillmentTime &&
        fulfillmentDropdown ? (
          <View style={styles.emptyCartFulfillmentCard}>
            {fulfillmentDropdown}
          </View>
        ) : null}
      </>
    ),
    [
      alerts,
      desiredFulfillmentTime,
      fulfillmentDropdown,
      multiCartEnabled,
      openAllCarts,
      openNewCartFromTabs,
      searchTerm,
      searching,
      setSearchTerm,
      showFulfillmentOnlySubheader,
      showNoSearchResults,
      styles.emptyCartFulfillmentCard,
    ],
  );

  return (
    <TimeToFullDisplay record={orderSummaryVisible}>
      <StatusBar backgroundColor={theme.colors.white} barStyle="dark-content" />
      <PageContainer
        containerStyle={
          orderSummarySectionsEnabled
            ? styles.pageContainerSections
            : styles.pageContainer
        }
        navigationHeaderProps={{
          ...navigationHeaderProps,
          actions: [
            {
              Icon: MenuHorizontalIcon,
              iconProps: {
                height: 24,
                stroke: theme.colors.primary,
                width: 24,
              },
              isIconFillEmpty: true,
              onPress: () => setIsOrderSummaryConfigOpen(true),
              testID: 'show-order-summary-settings',
            },
          ],
          centerTitle: true,
          subheader: hasResponse ? (
            <>
              {!orderSummarySectionsEnabled ? (
                <View style={styles.subheader}>
                  <FulfillmentButton
                    containerStyle={styles.fulfillmentButtonContainer}
                    fulfillmentDate={
                      order?.restaurant_desired_delivery_time || null
                    }
                    fulfillmentType={orderFulfillmentType}
                    loading={isUpdatingFulfillment}
                    onPress={() => setShowFulfillmentSelector(true)}
                    style={styles.fulfillmentButton}
                    timeslot={fulfillmentTimeslot}
                    trackingData={{
                      name: 'order_summary.fulfillment_button',
                      properties: {
                        orderUUID: draftOrderUUID,
                        smartOrderUUID,
                      },
                    }}
                  />
                  <View style={styles.subheaderInfo}>
                    <Text style={TYPOGRAPHY.medium}>
                      {i18n.t(
                        unitCount === 1 ? 'unit_count' : 'unit_count_plural',
                        {
                          count: unitCount,
                        },
                      )}
                    </Text>
                    {subtotal ? (
                      <View style={styles.subheaderSubtotal}>
                        <Text style={[TYPOGRAPHY.medium, FONTS.primaryBold]}>
                          {subtotal}
                        </Text>
                      </View>
                    ) : null}
                  </View>
                </View>
              ) : null}
              {showFulfillmentOnlySubheader &&
              desiredFulfillmentTime &&
              !fulfillmentDropdown ? (
                <View style={styles.emptyCartFulfillment}>
                  <EditFulfillmentDateButton
                    date={desiredFulfillmentTime}
                    fulfillmentType={orderFulfillmentType}
                    onPress={() => setShowFulfillmentSelector(true)}
                    testID="edit-fulfillment-date-button"
                    trackingData={{
                      name: 'order_summary.fulfillment_button',
                      properties: {
                        orderUUID: draftOrderUUID,
                        smartOrderUUID,
                      },
                    }}
                  />
                </View>
              ) : null}
            </>
          ) : null,
          title: (
            <View style={styles.titleContainer}>
              <Text style={TYPOGRAPHY.navigationCenterTitle}>
                {i18n.t('cart')}
              </Text>
              {restaurantRelationships.length > 1 ? (
                <MultiStyleText
                  defaultStyle={TYPOGRAPHY.small}
                  emphasisStyle={[TYPOGRAPHY.small, FONTS.primaryBold]}
                >
                  {i18n.t('order_for_restaurant', {
                    restaurantName: selectedRestaurantRelationship.name,
                  })}
                </MultiStyleText>
              ) : null}
            </View>
          ),
        }}
      >
        {isOrderSummaryConfigOpen ? (
          <OrderSummarySettingsActionSheet
            draftOrderUUID={draftOrderUUID}
            navigateBack={navigateBack}
            onCartCleared={resetCartContent}
            onClose={() => setIsOrderSummaryConfigOpen(false)}
            onEditCart={(cart) => {
              setIsOrderSummaryConfigOpen(false);
              openEditCart(cart, 'settings');
            }}
          />
        ) : null}
        {multiCartEnabled ? (
          <>
            {/* Mounted only while open: the sheet reports onClose after its own slide-out, which is what lets the form it hands off to open without the two cross-fading. */}
            {isAllCartsOpen ? (
              <AllCartsSheet
                onAddCart={() => openNewCart('all_carts')}
                onClose={closeAllCarts}
                onEdit={(cart) => openEditCart(cart, 'all_carts')}
              />
            ) : null}
            {isCartFormOpen ? (
              <CartFormActionSheet
                editingCart={editingCart}
                onCancel={cancelCartFlow}
                onSaved={handleCartSaved}
              />
            ) : null}
          </>
        ) : null}
        <View
          onLayout={(e) => {
            if (hasResponse) {
              // Wait for data before measuring the container
              onContainerLayout?.(e);
            }
          }}
          ref={containerRef}
          style={styles.wrapper}
        >
          {!hasResponse || isCartContentLoading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator />
            </View>
          ) : (
            <>
              <View
                ref={(ref) => {
                  // Measure so the bottom inset on the list accounts for the page footer
                  ref?.measureInWindow((_x, y, _width, height) => {
                    setKeyboardExtraSpacing(
                      // Include extra space so the keyboard isn't flush with the input
                      -(windowDimensions.height - y - height - bottom) + 20,
                    );
                  });
                }}
                style={[
                  styles.wrapper,
                  { paddingBottom: listContainerPaddingBottom },
                ]}
              >
                <OrderSummaryList
                  availableQuantityMap={availableQuantityMap}
                  focusedIndex={focusedIndex}
                  fulfillmentButtonElement={fulfillmentDropdown}
                  fulfillmentType={orderFulfillmentType}
                  itemPromotionsMap={itemPromotionsMap}
                  key={draftOrderUUID ?? 'empty-cart'}
                  keyboardAwareProps={{
                    extraSpacing: keyboardExtraSpacing,
                  }}
                  ListFooterComponent={
                    <>
                      {showPromotionBadge ? (
                        <>
                          {orderSummarySectionsEnabled ? null : (
                            <ListItemSeparator />
                          )}
                          <View style={styles.promotionBadgeContainer}>
                            <OrderSummaryPromotions
                              appliedPromotions={appliedPromotions}
                              currencyCode={currencyCode}
                            />
                          </View>
                        </>
                      ) : null}
                      {showRecommendations ? (
                        <OrderSummaryRecommendations
                          updateQuantity={(updateQuantityData) => {
                            setIsUpdateQuantityPending(true);
                            dispatchQuantityUpdateAction({
                              payload: {
                                ...updateQuantityData,
                                source: 'summary_recommendations',
                              },
                            });
                            debouncedUpdateAndClear({}, draftOrderUUID);
                          }}
                        />
                      ) : null}
                      {showOrderSummaryInputs && draftOrderUUID ? (
                        <View
                          style={
                            orderSummaryInputsNeedsTopSpacing
                              ? styles.orderSummaryInputsContainer
                              : null
                          }
                        >
                          <OrderSummaryInputs
                            appliedDiscountCodes={
                              (data?.validateOrder.order?.discount_codes ??
                                []) as string[]
                            }
                            disabled={orderInputsDisabled}
                            draftOrderUUID={draftOrderUUID}
                            errorMap={additionalInputErrorMap}
                            inputs={additionalInputs}
                            listRef={listRef}
                            onChange={setAdditionalInputValue}
                            onDiscountCodesApplied={validateOrderCallback}
                            showDiscountCodeInput={isOnlineWithoutSearch}
                            valueMap={additionalInputValueMap}
                          />
                        </View>
                      ) : null}
                      {payUponOrdering && !isOfflineModeActive ? (
                        <OrderPaymentMethodSelector
                          loadingPaymentMethod={loadingPaymentMethod}
                          navigateToPaymentMethod={navigateToPaymentMethod}
                          paymentMethodValidationMessage={
                            paymentMethodValidationMessage
                          }
                          selectedPaymentMethod={selectedPaymentMethod}
                        />
                      ) : null}
                      {requiresPaymentMethodOnFileAlert &&
                      !isOfflineModeActive ? (
                        <OrderPaymentMethodSelector
                          loadingPaymentMethod={false}
                          navigateToPaymentMethod={navigateToPaymentMethod}
                          paymentMethodValidationMessage={i18n.t(
                            'require_payment_method_on_file',
                          )}
                          selectedPaymentMethod={null}
                        />
                      ) : null}
                      {searchTerm ? (
                        <Button
                          containerStyle={styles.showAllButtonContainer}
                          onPress={clearSearch}
                          rightElement={
                            <ChevronDown fill={theme.colors.black} />
                          }
                          rightElementStyle={styles.searchChevronIconContainer}
                          rounded
                          style={[
                            styles.showAllButton,
                            showSplitOrderSections
                              ? styles.showAllButtonSection
                              : null,
                          ]}
                          trackingData={{
                            name: 'order_summary.show_all_items',
                            properties: {
                              orderUUID: draftOrderUUID,
                            },
                          }}
                          type="secondary"
                        >
                          {i18n.t(
                            orderItems.length > 1
                              ? 'show_all_items_in_cart.plural'
                              : 'show_all_items_in_cart.single',
                            {
                              count: orderItems.length,
                            },
                          )}
                        </Button>
                      ) : null}
                    </>
                  }
                  ListHeaderComponent={listHeader}
                  listRef={listRef}
                  loyaltyPointsMap={loyaltyPointsMap}
                  onChooseSubstitutionPress={async (
                    itemWithSubstitute: ItemWithSubstitute<ItemListOption>,
                  ) => {
                    const selectedDate = selectedFulfillment?.date;
                    if (selectedDate) {
                      const formattedFulfillmentDate =
                        moment(selectedDate).format('YYYY-MM-DD');

                      const originalVariantPackUUID =
                        itemWithSubstitute.substitution?.original_variant_pack
                          .uuid;
                      await chooseSubstitution({
                        fulfillmentDate: formattedFulfillmentDate,
                        itemWithSubstitute,
                        orderUUID: draftOrderUUID!,
                        variantPacksInOrder: new Set(
                          orderItems.map((oi) => oi.variants_pack.uuid),
                        ),
                      });
                      if (originalVariantPackUUID) {
                        // clear local quantity state so quantity is set from validation
                        // after substitution
                        dispatchQuantityReducerAction({
                          payload: {
                            variantPackUUIDs: [originalVariantPackUUID],
                          },
                          type: OrderQuantityActions.CLEAR,
                        });
                      }
                    }
                  }}
                  onClearSubstitutionPress={onClearSubstitutionPress}
                  onDoNotSubstitutePress={onDoNotSubstitutePress}
                  onEditDatePress={() => setShowFulfillmentSelector(true)}
                  onEditOrderItemNote={
                    editOrderItemNoteWithSkipNextFocusValidation
                  }
                  onOrderItemNotesSaved={onOrderItemNotesSaved}
                  onSalesRepPriceUpdated={async () => {
                    // Re-validate the order to refresh order item prices
                    await validateOrderCallback();
                  }}
                  onVisible={onOrderSummaryVisible}
                  orderUUID={draftOrderUUID!}
                  quantityUpdatesPending={isUpdateQuantityPending}
                  removeItems={removeItems}
                  resetDisabled={Boolean(initialSubstitutionChoice)}
                  searchTerm={searchTerm}
                  sectionMetadataMap={sectionMetadataMap}
                  sections={sectionsWithFlatIndex}
                  skipInteractionCheck={skipListInteractionCheck}
                  smartOrderUUID={smartOrderUUID}
                  updateQuantity={(updateQuantityData) => {
                    setIsUpdateQuantityPending(true);
                    dispatchQuantityUpdateAction({
                      payload: {
                        ...updateQuantityData,
                        source: 'order_summary',
                      },
                    });
                    debouncedUpdateAndClear({}, draftOrderUUID);
                  }}
                />
              </View>
              {showPageFooter ? (
                <View onLayout={onPageFooterLayout} style={styles.pageFooter}>
                  <View style={styles.deliveryDateRow}>
                    <OrderSummaryFooter
                      calculatedTransactionFee={calculatedTransactionFee}
                      displayItemPrices={
                        selectedRestaurantRelationship.displayItemPrices
                      }
                      includeSubtotal={false}
                      loading={isUpdateQuantityPending || isValidating}
                      loyalty={loyalty}
                      onLoyaltyEnrolled={validateOrderCallback}
                      order={order || null}
                      orderFees={orderFees}
                      orderMinimumErrorCount={orderMinimumErrorCount}
                      totalPrice={totalPrice}
                      transactionFeeName={
                        selectedRestaurantRelationship.transactionFeeName
                      }
                    />
                  </View>
                  {!searchTerm ? (
                    <Button
                      {...sharedSubmitButtonProps}
                      containerStyle={styles.buttonWrapper}
                      onPress={handlePlaceOrderPress}
                      size="large"
                      style={styles.placeOrder}
                      testID="place-order-button"
                      trackingData={{
                        name: isOfflineModeActive
                          ? 'order_summary.queue_order_button'
                          : 'order_summary.place_order_button',
                        properties: {
                          isFallbackOrder: Boolean(selectedFallbackOption),
                          orderUUID: draftOrderUUID,
                        },
                      }}
                    >
                      {placeOrderButtonText}
                    </Button>
                  ) : null}
                  {orderQueuingDisabled ? (
                    <View style={styles.queueOrderBlocked}>
                      <OfflineModeIcon />
                      <Typography variant="paragraph-md">
                        {i18n.t('go_online_to_place_order')}
                      </Typography>
                    </View>
                  ) : null}
                </View>
              ) : null}
            </>
          )}
        </View>
        {isFallbackSheetOpen && selectedFallbackOption ? (
          <FallbackConfirmationActionSheet
            fulfillmentDate={desiredFulfillmentTime!}
            isVisible
            onClose={() => setFallbackSheetOpen(false)}
            onContinue={() => {
              Analytics.track('order_summary.fallback_confirmation.continue', {
                orderUUID: draftOrderUUID,
                type: selectedFallbackOption.fulfillment_name,
              });
              isFallbackConfirmed.current = true;
              setFallbackSheetOpen(false);
              checkConfirmationsAndSubmitOrder();
            }}
          />
        ) : null}
        {isSameDaySheetOpen ? (
          <SameDayConfirmationActionSheet
            fulfillmentType={orderFulfillmentType}
            isVisible
            onClose={() => setSameDaySheetOpen(false)}
            onConfirmDeliveryDate={() => {
              isSameDayConfirmed.current = true;
              setSameDaySheetOpen(false);
              checkConfirmationsAndSubmitOrder();
            }}
            onEditDeliveryDate={() => {
              setShowFulfillmentSelector(true);
              setSameDaySheetOpen(false);
            }}
          />
        ) : null}
        {selectedFulfillment && showFulfillmentSelector ? (
          <FulfillmentActionSheet
            draftOrder={draftOrder}
            fulfillmentOptions={selectableFulfillmentOptions}
            initialSelectedFulfillment={selectedFulfillment}
            isLoading={isUpdateQuantityPending || isUpdatingQuantity}
            onClose={() => setShowFulfillmentSelector(false)}
            onSave={async (newFulfillment) => {
              isSameDayConfirmed.current = false;
              await onSaveFulfillment(newFulfillment);
            }}
            trackingProperties={{
              smartOrderUUID,
              source: 'order_summary',
            }}
          />
        ) : null}
        {isSubstitutionSheetOpen ? (
          <SubstitutionConfirmationActionSheet
            onChooseSubstitute={() => {
              listRef.current?.scrollToIndex({
                animated: true,
                index: firstUnactionedSubIndex ?? 0,
              });
              isEssentialItemSheetConfirmed.current = false;
              setSubstitutionSheetOpen(false);
            }}
            onClose={() => {
              isEssentialItemSheetConfirmed.current = false;
              setSubstitutionSheetOpen(false);
            }}
            onSubmitOrder={() => {
              isSubstitutionConfirmed.current = true;
              setSubstitutionSheetOpen(false);
              checkConfirmationsAndSubmitOrder();
            }}
            submitOrderText={placeOrderButtonText}
          />
        ) : null}
        {isEssentialSheetOpen ? (
          <EssentialItemReminderSheet
            essentialItemUUIDs={missingEssentialItems}
            footer={
              <View style={styles.reminderFooter}>
                <View style={styles.essentialFooterWrapper}>
                  <OrderSummaryFooter
                    calculatedTransactionFee={calculatedTransactionFee}
                    displayItemPrices={
                      selectedRestaurantRelationship.displayItemPrices
                    }
                    includeSubtotal={false}
                    loading={isUpdateQuantityPending || isValidating}
                    loyalty={loyalty}
                    onLoyaltyEnrolled={validateOrderCallback}
                    order={order || null}
                    orderFees={orderFees}
                    orderMinimumErrorCount={orderMinimumErrorCount}
                    totalPrice={totalPrice}
                  />
                </View>
                <Button
                  {...sharedSubmitButtonProps}
                  containerStyle={styles.reminderButtonWrapper}
                  onPress={onSubmitEssentialItems}
                  trackingData={{
                    name: isOfflineModeActive
                      ? 'reminder_sheet.queue_order_button'
                      : 'reminder_sheet.place_order_button',
                    properties: {
                      isFallbackOrder: Boolean(selectedFallbackOption),
                      orderUUID: draftOrderUUID,
                    },
                  }}
                >
                  {placeOrderButtonText}
                </Button>
              </View>
            }
            onClose={() => {
              validateOrderCallback();
              setEssentialSheetOpen(false);
            }}
            onError={() => {
              if (!essentialItemErrorCalled.current) {
                essentialItemErrorCalled.current = true;
                setEssentialSheetOpen(false);
                sendOrderToBackend();
              }
            }}
            onSubmitEssentialItems={onSubmitEssentialItems}
            updateQuantity={(updateQuantityData) => {
              setIsUpdateQuantityPending(true);
              dispatchQuantityUpdateAction({
                payload: {
                  ...updateQuantityData,
                  source: 'reminder_sheet',
                },
              });
              debouncedUpdateAndClear({}, draftOrderUUID);
            }}
          />
        ) : null}
        {orderAvailability.selectedAvailability &&
        isTimeslotActionSheetOpen &&
        selectedFulfillment ? (
          <FulfillmentActionSheet
            draftOrder={draftOrder}
            fulfillmentOptions={selectableFulfillmentOptions}
            initialRoute={FulfillmentActionSheetRoute.TimeslotSelect}
            initialSelectedFulfillment={selectedFulfillment}
            initialSelectedFulfillmentOption={
              orderAvailability.selectedAvailability
            }
            initialTimeslotOptions={availableTimeslots}
            isLoading={isUpdateQuantityPending || isUpdatingQuantity}
            onClose={() => {
              setTimeslotActionSheetOpen(false);
            }}
            onSave={async (newFulfillment) => {
              isSameDayConfirmed.current = false;
              const saved = await onSaveFulfillment(newFulfillment, {
                skipRevalidation: true,
              });

              if (saved) {
                checkConfirmationsAndSubmitOrder(true);
              }
            }}
            trackingProperties={{
              smartOrderUUID,
              source: 'order_summary_timeslots',
            }}
          />
        ) : null}
      </PageContainer>
    </TimeToFullDisplay>
  );
};

interface ScreenProps {
  navigation: NavigationProp<AppModalStackParams>;
  route: RouteProp<NewOrderStackParams, NEW_ORDER_ROUTES.OrderSummary>;
}

export const OrderSummary: React.FC<ScreenProps> = ({ navigation, route }) => {
  const { showActionSheet } = useActionSheetManagerContext();
  const { showErrorToast } = useToasts();
  const [isFocused, setFocused] = useState(true);
  const isClosableRef = useRef(true);
  const getSubstitutionOptions = useGetSubstitutionOptions();
  const {
    draftOrder,
    multiCartEnabled,
    restaurantUUID,
    supplierUUID,
    switchActiveOrder,
  } = useNewOrderContext();
  const substitutionCatalogSearchEnabled =
    useSubstitutionCatalogSearchEnabled();
  const { replaceOrderQuantities } = useNewOrderActionsContext();
  const { loading, selectedPaymentMethod, setPaymentContextState } =
    usePaymentsContext();
  const isOfflineModeActive = useOfflineModeActive();
  const { offlineOrderContext } = useOfflineOrderContext(
    restaurantUUID!,
    supplierUUID!,
  );
  const addRemovedOrderUUID = useOrderInboundsStore(
    (state) => state.actions.addRemovedOrderUUID,
  );
  const clearPendingSmartOrders = useOrderInboundsStore(
    (state) => state.actions.clearPendingSmartOrders,
  );
  const takePendingReviewTiming = useOrderInboundsStore(
    (state) => state.actions.takePendingReviewTiming,
  );
  const smartOrderParams = useSmartOrderParams();
  const smartOrderNavParam = route.params?.smartOrderUUID;

  // Otherwise a staged-but-abandoned timing sits around and gets
  // attributed to whatever unrelated order submits next.
  const abandonPendingReviewTiming = useCallback(() => {
    if (smartOrderNavParam) {
      takePendingReviewTiming(smartOrderNavParam);
    }
  }, [smartOrderNavParam, takePendingReviewTiming]);

  const navigateToEditOrderItemNote = useNavigateToEditOrderItemNote();

  const initialQuantityMap = route.params?.initialQuantityMap;

  useEffect(() => {
    // Suppress warning from web because BackHandler isn't supported anymore
    // https://github.com/react-navigation/react-navigation/issues/11316
    if (Platform.OS === 'android') {
      const backHandler = BackHandler.addEventListener(
        'hardwareBackPress',
        () => {
          if (!isClosableRef.current) {
            // prevent default back handler from navigating away
            return true;
          }
          if (initialQuantityMap) {
            replaceOrderQuantities(initialQuantityMap, 'order_summary');
          }
          clearPendingSmartOrders();
          abandonPendingReviewTiming();
          // ensure default back handler runs
          return false;
        },
      );
      return () => {
        backHandler.remove();
      };
    }
  }, [
    replaceOrderQuantities,
    clearPendingSmartOrders,
    abandonPendingReviewTiming,
    initialQuantityMap,
  ]);

  const onFocusCallback = useCallback(() => {
    setFocused(true);
  }, []);

  const onBlurCallback = useCallback(() => {
    setFocused(false);
  }, []);

  useComponentFocus({
    initialFocus: true,
    onBlur: onBlurCallback,
    onFocus: onFocusCallback,
  });

  const navigateBack = useCallback(() => {
    if (isClosableRef.current) {
      navigation.navigate(APP_CONTAINER_ROUTES.Base);
    }
  }, [navigation]);

  const clearSubChoice = useCallback(() => {
    navigation.setParams({ initialSubstitution: undefined });
  }, [navigation]);

  const navigateToPaymentMethod = useCallback(() => {
    navigation.navigate(NEW_ORDER_ROUTES.PaymentMethod, {
      onSavePaymentMethod: () => {
        // pass merge: true to preserve nav params from the original order
        // summary entry point (like smartOrderUUID).
        navigation.navigate({
          merge: true,
          name: NEW_ORDER_ROUTES.OrderSummary,
          params: undefined,
        });
      },
      onSelectPaymentMethod: (paymentMethod?: PaymentMethodOption) => {
        setPaymentContextState({
          selectedPaymentMethod: paymentMethod,
        });
      },
      requireSavedPaymentMethod: true,
    });
  }, [navigation, setPaymentContextState]);

  const setCloseable = useCallback((isClosable: boolean) => {
    isClosableRef.current = isClosable;
  }, []);

  const navHeaderProps: Partial<NavigationHeaderProps> = initialQuantityMap
    ? {
        BackButtonIcon: DeleteIcon,
        onBackPress: () => {
          if (!isClosableRef.current) {
            return;
          }
          navigation.goBack();
          clearPendingSmartOrders();
          abandonPendingReviewTiming();
          replaceOrderQuantities(initialQuantityMap, 'order_summary');
        },
        showBackButton: true,
      }
    : {
        onBackPress: () => {
          if (!isClosableRef.current) {
            return;
          }
          navigation.goBack();
          abandonPendingReviewTiming();
        },
        showBackButton: true,
      };

  return (
    <OrderSummaryComponent
      chooseSubstitution={async ({
        itemWithSubstitute,
        fulfillmentDate,
        orderUUID,
        variantPacksInOrder,
      }: ChooseSubstitutionParams) => {
        try {
          const variantPackUUID =
            itemWithSubstitute.substitution!.original_variant_pack.uuid;
          const { substitutionListOptions, availabilityMap } =
            await getSubstitutionOptions({
              fulfillmentDate,
              variantPacksInOrder,
              variantPackUUID,
            });

          if (
            substitutionListOptions.length === 0 &&
            substitutionCatalogSearchEnabled
          ) {
            navigation.navigate(NEW_ORDER_ROUTES.Substitutions, {
              params: {
                fulfillmentDate,
                itemWithSubstitute,
                orderUUID,
                variantPacksInOrder,
              },
              screen: SUBSTITUTION_ROUTES.SearchCatalog,
            });
          } else {
            navigation.navigate(NEW_ORDER_ROUTES.Substitutions, {
              params: {
                availabilityMap,
                fulfillmentDate,
                itemWithSubstitute,
                orderUUID,
                substitutionListOptions,
                variantPacksInOrder,
              },
              screen: SUBSTITUTION_ROUTES.ChooseSubstitution,
            });
          }
        } catch (e) {
          showErrorToast();
        }
      }}
      clearInitialSubstitutionChoice={clearSubChoice}
      editOrderItemNote={navigateToEditOrderItemNote}
      hasFocus={isFocused}
      initialSubstitutionChoice={route.params?.initialSubstitution}
      loadingPaymentMethod={loading}
      navigateBack={navigateBack}
      navigateToPaymentMethod={navigateToPaymentMethod}
      navigationHeaderProps={navHeaderProps}
      onOrderPlaced={async (props) => {
        const draftOrderUUID = draftOrder?.uuid;
        const matchingOrder = offlineOrderContext.data?.getOrders.find(
          (o) => o.order?.uuid === draftOrderUUID,
        );
        const smartOrderUUID =
          smartOrderNavParam || smartOrderParams?.smartOrderUUID;
        if (smartOrderUUID) {
          addRemovedOrderUUID(smartOrderUUID);
        }
        if (smartOrderParams && draftOrderUUID) {
          navigation.navigate(SMART_ORDER_CONTAINER_ROUTES.PlacedOrder, {
            orderUUID: draftOrderUUID,
            submittedOrderProps: props,
          });
        } else if (isOfflineModeActive && matchingOrder) {
          showActionSheet(OrderQueuedActionSheet);
          navigation.navigate(APP_CONTAINER_ROUTES.Base);
        } else if (smartOrderNavParam && initialQuantityMap) {
          showActionSheet(
            OrderSubmittedActionSheet,
            props as OrderSubmittedComponentProps,
          );
          navigation.navigate(CONTAINER_MODAL_ROUTES.OrderInbounds, {
            screen: ORDER_INBOUNDS_ROUTES.List,
          });
        } else {
          showActionSheet(
            OrderSubmittedActionSheet,
            props as OrderSubmittedComponentProps,
          );
          navigation.navigate(APP_CONTAINER_ROUTES.Base);
        }
        if (multiCartEnabled) {
          void switchActiveOrder(null).catch(() => {});
        }
      }}
      selectedPaymentMethod={selectedPaymentMethod}
      setCloseable={setCloseable}
      smartOrderUUID={
        smartOrderNavParam || smartOrderParams?.smartOrderUUID || null
      }
    />
  );
};

const useStyles = createStyles((theme) => ({
  buttonWrapper: {
    paddingTop: 8,
  },
  deliveryDateRow: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginHorizontal: SCREEN_PADDING,
    marginTop: 16,
  },
  emptyCartFulfillment: {
    backgroundColor: theme.colors.white,
    paddingBottom: 12,
    paddingHorizontal: 16,
  },
  emptyCartFulfillmentCard: {
    backgroundColor: theme.colors.white,
    borderRadius: SECTION_BORDER_RADIUS,
    marginTop: 16,
    padding: LIST_MARGIN_HORIZONTAL,
  },
  essentialFooterWrapper: {
    flexDirection: 'row',
  },
  fallbackToast: {
    flexDirection: 'row',
  },
  fallbackToastContent: {
    flex: 1,
  },
  fallbackToastIcon: {
    marginRight: theme.sk.spacing.spaceSMedium,
  },
  fallbackToastMessage: {
    ...FONTS.primary,
    marginTop: theme.sk.spacing.spaceXsmall,
  },
  fulfillmentButton: {
    paddingHorizontal: 8,
  },
  fulfillmentButtonContainer: {
    maxWidth: '50%',
  },
  loadingContainer: {
    marginTop: SCREEN_PADDING,
  },
  orderSummaryInputsContainer: {
    marginTop: theme.sk.spacing.spaceMedium,
  },
  pageContainer: {
    backgroundColor: theme.colors.white,
  },
  pageContainerSections: {
    backgroundColor: theme.colors.background,
  },
  pageFooter: {
    ...shadow({
      color: theme.colors.black,
      elevation: 5,
      opacity: 0.1,
      radius: 4,
      x: 0,
      y: -4,
    }),
    backgroundColor: theme.colors.white,
    borderTopColor: theme.colors.stroke,
    paddingBottom: SCREEN_PADDING_LARGE,
  },
  placeOrder: {
    marginHorizontal: SCREEN_PADDING,
  },
  promotionBadgeContainer: {
    paddingVertical: 8,
  },
  queueOrderBlocked: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'center',
    marginTop: 8,
  },
  reminderButtonWrapper: {
    marginTop: 8,
  },
  reminderFooter: {
    borderTopColor: theme.colors.stroke,
    borderTopWidth: 1,
    paddingHorizontal: 16,
    paddingTop: 16,
  },
  searchChevronIconContainer: {
    marginLeft: 8,
  },
  showAllButton: {
    backgroundColor: theme.sk.color.bg.secondary.default,
    height: 32,
    marginHorizontal: 'auto',
  },
  showAllButtonContainer: {
    marginVertical: 16,
  },
  showAllButtonSection: {
    backgroundColor: theme.colors.white,
  },
  subheader: {
    alignItems: 'center',
    backgroundColor: theme.colors.availabilityBackground,
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  subheaderInfo: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  subheaderSubtotal: {
    borderLeftColor: theme.colors.primary,
    borderLeftWidth: 1,
    marginLeft: 4,
    paddingLeft: 4,
  },
  titleContainer: {
    alignItems: 'center',
  },
  wrapper: {
    flex: 1,
  },
}));
